// src/AppWithFirebase.js - Enhanced App with Firebase Integration

import React, { useState, useRef, useEffect, useCallback, useMemo, lazy, Suspense } from 'react';
import { getBracketNodePositions } from './utils/layoutUtils';
import { capitalizeName, shuffleArray } from './utils/nameUtils';
import { useLocalStorage } from './hooks/useLocalStorage';
import { useResponsive } from './hooks/useResponsive';
import { TOURNAMENT_FORMATS } from './types/tournamentTypes';
import { createTournamentManager } from './utils/tournamentManager';
import { swapPlayersInMatches, canRearrangePlayers } from './utils/dragDropUtils';
import { assignTablesToMatches, shuffleTableAssignments, assignFreedTable, reassignTablesAfterManualChange, getUnusedTables } from './utils/tableAssignmentUtils';

// Tournament components
import Bracket from './components/tournament/Bracket';
import Sidebar from './components/tournament/Sidebar';
import TableView from './components/tournament/TableView';

// Firebase components
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { useAdmin } from './contexts/AdminContext';
import { useTournament, useTournamentOperations } from './hooks/useTournaments';
import { updateTournamentAdmin } from './firebase/admin';
import AuthModal from './components/auth/AuthModal';
import ShareTournamentModal from './components/sharing/ShareTournamentModal';
import LandingNavigation from './components/navigation/LandingNavigation';
import BrandingModal from './components/dashboard/BrandingModal';

// Page components (landing is eager — it's the default view)
import LandingPage from './components/pages/LandingPage';

// Self-scoring / bracket utilities
import { generateMatchTokens } from './utils/tokenUtils';
import { readBracketAsArray, bracketArrayToMap, diffBracketMaps, SCHEMA_VERSION, stripSourceMatch, bracketHasSourceMatchBloat, getBracketPlayers } from './utils/bracketSchema';
import { sendMatchReadyNotification, deleteTournamentSmsDocuments, sendOptInConfirmation } from './firebase/sms';
import { deleteAllPendingScores } from './firebase/scoring';
// Recurring-instance subcollection writes (owner & admin pass Firestore rules directly).
import { writeInstanceBracketDiff, updateInstanceIndex, setInstance } from './firebase/tournaments';
// Recovery primitives for a wedged sync channel (expired token / slept tab).
import { auth, forceReconnect, forceTokenRefresh, writesAreFlushed } from './firebase/config';
import { onIdTokenChanged } from 'firebase/auth';
import SyncAlertBanner from './components/common/SyncAlertBanner';

// Code-split heavy / route-level components out of the initial bundle so the
// landing page doesn't download the maps (leaflet), admin, scoring, public
// viewer, etc. A single <Suspense> boundary wraps the app (see App() below).
const PublicTournamentView = lazy(() => import('./components/sharing/PublicTournamentView'));
const ProfilePage = lazy(() => import('./components/dashboard/ProfilePage'));
const PlayerProfile = lazy(() => import('./components/profile/PlayerProfile'));
const ScheduledTournamentView = lazy(() => import('./components/tournament/ScheduledTournamentView'));
const ContactPage = lazy(() => import('./components/pages/ContactPage'));
const PrivacyPage = lazy(() => import('./components/pages/PrivacyPage'));
const TermsPage = lazy(() => import('./components/pages/TermsPage'));
const HelpPage = lazy(() => import('./components/help/HelpPage'));
const TournamentFinder = lazy(() => import('./components/features/TournamentFinder')); // pulls in react-leaflet
const Leaderboard = lazy(() => import('./components/features/Leaderboard'));
const AdminDashboard = lazy(() => import('./components/admin/AdminDashboard'));
const PlayerScoreEntry = lazy(() => import('./components/scoring/PlayerScoreEntry'));
const StreamingOverlay = lazy(() => import('./components/streaming/StreamingOverlay'));

// Lightweight sync diagnostics ring buffer. Records timestamped events (writes,
// server-ack transitions, token refreshes, tab wake, auth errors) so a stuck
// occurrence can be root-caused after the fact — the failure mode is silent
// (writes queue in the local cache with no thrown error), so we need our own
// timeline. Inspect live in DevTools via `window.__syncDiag`.
const logSyncDiag = (event, detail) => {
  const entry = { t: new Date().toISOString(), event, detail: detail ?? null };
  if (typeof window !== 'undefined') {
    if (!window.__syncDiag) window.__syncDiag = [];
    window.__syncDiag.push(entry);
    if (window.__syncDiag.length > 500) window.__syncDiag.shift();
  }
  // eslint-disable-next-line no-console
  console.log('[syncDiag]', entry.t, event, detail !== undefined ? detail : '');
};

// Sync-recovery tuning.
const SYNC_STUCK_MS = 20000;              // server ack overdue -> red LED + start recovery
const KICK_THROTTLE_MS = 15000;           // min gap between channel kicks (avoid thrash)
const WATCHDOG_MS = 30000;                // recovery retry cadence while stuck
const TOKEN_REFRESH_MS = 25 * 60 * 1000;  // proactive token refresh (safely under the ~1h expiry)

// Out-of-band sync-failure beacon. Posted via navigator.sendBeacon (a text/plain
// "simple" request → no CORS preflight) so it survives tab close and doesn't rely
// on the possibly-wedged Firestore write stream. Bump APP_SYNC_VERSION each deploy
// so we can tell which build a director actually ran when a report lands.
const SYNC_DIAG_URL = 'https://us-central1-kcpooltournament.cloudfunctions.net/collectSyncDiag';
const APP_SYNC_VERSION = '2026-08-16-syncfix2';

// Build the tiny parent-doc summary the navigator lists occurrences from.
const buildInstanceSummary = ({ completed, inProgress, canceled, winner, playerCount, tournamentEndTime, matchCount, completedMatches }) => ({
  status: canceled ? 'canceled' : completed ? 'completed' : (inProgress ? 'inProgress' : 'scheduled'),
  completed: !!completed,
  inProgress: !!inProgress,
  canceled: !!canceled,
  winner: winner || null,
  playerCount: playerCount || 0,
  // Match progress so the dashboard card can render matches/% for instance-backed
  // tournaments (recurring AND scheduled one-time) without reading the bracket doc.
  matchCount: matchCount || 0,
  completedMatches: completedMatches || 0,
  tournamentEndTime: tournamentEndTime || null,
  lastUpdated: new Date().toISOString(),
});

// Firebase sync utilities
// import { updateTournament, createTournament } from './firebase/tournaments';

// Performance optimization: lightweight bracket hash for change detection
// Much faster than JSON.stringify for large brackets
const getBracketStateHash = (bracket) => {
  if (!bracket || bracket.length === 0) return '';
  return bracket.map(m =>
    `${m.matchNum || ''}:${m.finished ? 1 : 0}:${m.winner || ''}:${m.p1?.name || m.player1 || ''}:${m.p2?.name || m.player2 || ''}`
  ).join('|');
};

// Stable callback wrapper (useEvent pattern): returns a function with a constant
// identity that always invokes the latest closure. Lets us pass handlers into
// memoized children (Bracket/MatchBox/TableView) without breaking their memo on
// every parent render, and without risking stale closures.
function useStableCallback(fn) {
  const ref = useRef(fn);
  ref.current = fn; // keep latest; only ever invoked from committed event handlers
  return useRef((...args) => ref.current(...args)).current;
}

// Helper to get initial state from URL (runs synchronously before first render)
function getInitialStateFromUrl() {
  const urlPath = window.location.pathname;

  // Check for host tournament URL - format: /host/{tournamentId} or /host/{tournamentId}/{instanceDate}
  // Instance date can be YYYY-MM-DD or full ISO timestamp YYYY-MM-DDTHH:MM:SS.sss
  const hostUrlMatch = urlPath.match(/^\/host\/([a-zA-Z0-9]+)(?:\/(\d{4}-\d{2}-\d{2}(?:T[\d:.]+)?))?$/);
  if (hostUrlMatch) {
    return {
      appMode: 'firebase',
      selectedTournament: { id: hostUrlMatch[1] },
      selectedInstanceDate: hostUrlMatch[2] || null,
      publicShareUrl: null,
      scoringToken: null,
      scoringTournamentId: null,
      overlayTournamentId: null
    };
  }

  // Check for tournament share URL
  const shareUrlMatch = urlPath.match(/^\/tournament\/([a-zA-Z0-9]+)$/);
  if (shareUrlMatch) {
    return { appMode: 'public', publicShareUrl: shareUrlMatch[1], selectedTournament: null, selectedInstanceDate: null, scoringToken: null, scoringTournamentId: null, overlayTournamentId: null };
  }

  // Check for player scoring URL - new format
  const scoreUrlMatchNew = urlPath.match(/^\/score\/([a-zA-Z0-9]+)\/([a-zA-Z0-9-]+)$/);
  if (scoreUrlMatchNew) {
    return { appMode: 'scoring', scoringTournamentId: scoreUrlMatchNew[1], scoringToken: scoreUrlMatchNew[2], selectedTournament: null, selectedInstanceDate: null, publicShareUrl: null, overlayTournamentId: null };
  }

  // Check for player scoring URL - old format
  const scoreUrlMatchOld = urlPath.match(/^\/score\/([a-zA-Z0-9-]+)$/);
  if (scoreUrlMatchOld) {
    return { appMode: 'scoring', scoringToken: scoreUrlMatchOld[1], scoringTournamentId: null, selectedTournament: null, selectedInstanceDate: null, publicShareUrl: null, overlayTournamentId: null };
  }

  // Check for streaming overlay URL
  const overlayUrlMatch = urlPath.match(/^\/overlay\/([a-zA-Z0-9]+)(?:\/(\d+))?$/);
  if (overlayUrlMatch) {
    return { appMode: 'overlay', overlayTournamentId: overlayUrlMatch[1], overlayTableNumber: overlayUrlMatch[2] ? parseInt(overlayUrlMatch[2], 10) : 1, selectedTournament: null, selectedInstanceDate: null, publicShareUrl: null, scoringToken: null, scoringTournamentId: null };
  }

  // Static pages
  if (urlPath === '/privacy') return { appMode: 'privacy', selectedTournament: null, selectedInstanceDate: null, publicShareUrl: null, scoringToken: null, scoringTournamentId: null, overlayTournamentId: null };
  if (urlPath === '/terms') return { appMode: 'terms', selectedTournament: null, selectedInstanceDate: null, publicShareUrl: null, scoringToken: null, scoringTournamentId: null, overlayTournamentId: null };
  if (urlPath === '/contact') return { appMode: 'contact', selectedTournament: null, selectedInstanceDate: null, publicShareUrl: null, scoringToken: null, scoringTournamentId: null, overlayTournamentId: null };
  if (urlPath === '/help') return { appMode: 'help', selectedTournament: null, selectedInstanceDate: null, publicShareUrl: null, scoringToken: null, scoringTournamentId: null, overlayTournamentId: null };
  if (urlPath === '/local') return { appMode: 'local', selectedTournament: null, selectedInstanceDate: null, publicShareUrl: null, scoringToken: null, scoringTournamentId: null, overlayTournamentId: null };
  if (urlPath === '/find') return { appMode: 'finder', selectedTournament: null, selectedInstanceDate: null, publicShareUrl: null, scoringToken: null, scoringTournamentId: null, overlayTournamentId: null };
  if (urlPath === '/leaderboard') return { appMode: 'leaderboard', selectedTournament: null, selectedInstanceDate: null, publicShareUrl: null, scoringToken: null, scoringTournamentId: null, overlayTournamentId: null };
  // Player profile share URL - /player/{uid}
  const playerUrlMatch = urlPath.match(/^\/player\/([a-zA-Z0-9_-]+)$/);
  if (playerUrlMatch) {
    return { appMode: 'player', viewPlayerId: playerUrlMatch[1], selectedTournament: null, selectedInstanceDate: null, publicShareUrl: null, scoringToken: null, scoringTournamentId: null, overlayTournamentId: null };
  }
  if (urlPath === '/dashboard') return { appMode: 'dashboard', selectedTournament: null, selectedInstanceDate: null, publicShareUrl: null, scoringToken: null, scoringTournamentId: null, overlayTournamentId: null };
  if (urlPath === '/admin') return { appMode: 'admin', selectedTournament: null, selectedInstanceDate: null, publicShareUrl: null, scoringToken: null, scoringTournamentId: null, overlayTournamentId: null };

  // Default to landing
  return { appMode: 'landing', selectedTournament: null, selectedInstanceDate: null, publicShareUrl: null, scoringToken: null, scoringTournamentId: null, overlayTournamentId: null };
}

// Get initial state once (before component mounts)
const initialUrlState = getInitialStateFromUrl();

function TournamentApp() {
  const { isMobile } = useResponsive();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { isAdminMode, adminManagedTournamentId, enterAdminMode, exitAdminMode } = useAdmin();
  const { create: createTournament } = useTournamentOperations();

  // App mode state - Initialize from URL to prevent flash/redirect issues on refresh
  const [appMode, setAppMode] = useState(initialUrlState.appMode);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalMode, setAuthModalMode] = useState('login'); // 'login' or 'signup'
  const [selectedTournament, setSelectedTournament] = useState(initialUrlState.selectedTournament);
  const [selectedInstanceDate, setSelectedInstanceDate] = useState(initialUrlState.selectedInstanceDate);
  const [publicShareUrl, setPublicShareUrl] = useState(initialUrlState.publicShareUrl);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showProfilePage, setShowProfilePage] = useState(false);
  const [viewPlayerId, setViewPlayerId] = useState(initialUrlState.viewPlayerId || null); // shared /player/{uid} link

  // Profile modal: prompt before closing if there are unsaved changes.
  const profileDirtyRef = useRef(false);
  const [highlightSaveTrigger, setHighlightSaveTrigger] = useState(0);
  const handleProfileDirty = useCallback((dirty) => { profileDirtyRef.current = dirty; }, []);
  const handleCloseProfile = useCallback(() => {
    if (profileDirtyRef.current) {
      const leave = window.confirm('You have unsaved changes to your profile. Leave without saving?');
      if (!leave) {
        // User chose to stay — nudge them to the Save button.
        setHighlightSaveTrigger((t) => t + 1);
        return;
      }
    }
    profileDirtyRef.current = false;
    setShowProfilePage(false);
  }, []);
  const [showBrandingModal, setShowBrandingModal] = useState(false);
  const [openFinderOnDashboard, setOpenFinderOnDashboard] = useState(false);
  const [openCreateModalOnDashboard, setOpenCreateModalOnDashboard] = useState(false);
  const [pendingSaveToCloud, setPendingSaveToCloud] = useState(false); // Track if user tried to save before auth
  const [scoringToken, setScoringToken] = useState(initialUrlState.scoringToken); // Token for player self-scoring page
  const [scoringTournamentId, setScoringTournamentId] = useState(initialUrlState.scoringTournamentId); // Tournament ID for scoring page
  const [overlayTournamentId, setOverlayTournamentId] = useState(initialUrlState.overlayTournamentId); // Tournament ID for streaming overlay
  const [overlayTableNumber, setOverlayTableNumber] = useState(initialUrlState.overlayTableNumber || 1); // Table number for streaming overlay

  // Existing local tournament state
  const [playersText, setPlayersText] = useState('');
  const [bracket, setBracketInternal] = useLocalStorage('tournamentBracket', []);

  const setBracket = setBracketInternal;
  // Always-current mirror of `bracket` for event handlers that must read fresh
  // state. MatchBox is memoized and intentionally skips re-rendering when an
  // unrelated match changes, so a handler closing over the `bracket` prop can
  // see stale data (e.g. a table a just-finished match still appears to hold).
  // Reading bracketRef.current sidesteps that without weakening the memo.
  const bracketRef = useRef([]);
  bracketRef.current = bracket;
  const [payoutAmounts, setPayoutAmounts] = useLocalStorage('payoutAmounts', ['']);
  const [numPayoutPlaces, setNumPayoutPlaces] = useLocalStorage('numPayoutPlaces', 3);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [customTableNumbers, setCustomTableNumbers] = useLocalStorage('customTableNumbers', '1,2,3,4,5,6,7,8');
  const [autoReassignTables, setAutoReassignTables] = useLocalStorage('autoReassignTables', false);
  const [lastPlayerList, setLastPlayerList] = useLocalStorage('lastPlayerList', '');
  const [viewMode, setViewMode] = useLocalStorage('viewMode', 'bracket');
  const [showDragDropTip, setShowDragDropTip] = useLocalStorage('showDragDropTip', true);
  const [showLegend, setShowLegend] = useLocalStorage('showLegend', true);
  const [tournamentFormat, setTournamentFormat] = useLocalStorage('tournamentFormat', TOURNAMENT_FORMATS.DOUBLE_ELIMINATION);
  const [nodePositions, setNodePositions] = useLocalStorage('nodePositions', {});
  const [raceLength, setRaceLength] = useLocalStorage('raceLength', 5);
  const [expectedTimePerMatch, setExpectedTimePerMatch] = useLocalStorage('expectedTimePerMatch', 30);
  const [hideBYEMatches, setHideBYEMatches] = useLocalStorage('hideBYEMatches', true);
  const [playerPayments, setPlayerPayments] = useLocalStorage('playerPayments', {}); // { playerName: { paid: boolean, timestamp: Date } }
  const [playerPhones, setPlayerPhones] = useLocalStorage('playerPhones', {}); // { playerName: { phone: string, phoneMasked: string } }
  const [playerUserIds, setPlayerUserIds] = useLocalStorage('playerUserIds', {}); // { playerName: odUserId } - maps player names to their user IDs
  const [textAlertsActivated, setTextAlertsActivated] = useState(false); // Whether SMS alerts have been sent for current bracket

  // Track whether stats have been processed for current tournament instance

  // Firebase tournament hook. Passing selectedInstanceDate subscribes to that
  // occurrence's own subcollection document (activeInstance) so live updates only
  // re-download that ~19 KB doc, not the whole parent.
  const {
    tournament: firebaseTournament,
    activeInstance,
    serverInstance,
    instanceMeta,
    loading: tournamentLoading,
    updateTournament: updateFirebaseTournamentDirect,
    mergeTournament: mergeFirebaseTournamentDirect
  } = useTournament(
    appMode === 'firebase' ? selectedTournament?.id : null,
    appMode === 'firebase' ? selectedInstanceDate : null
  );

  // Race-to targets used to cap manual score entry and auto-highlight the winner.
  // Firebase tournaments carry winners/losers race lengths on the parent doc;
  // local tournaments only have the single legacy `raceLength`. getMatchRaceTarget
  // (in raceUtils) picks winners vs. losers per match from this.
  const raceSettings = useMemo(() => ({
    winnersRaceLength: firebaseTournament?.winnersRaceLength || firebaseTournament?.raceLength || raceLength || 5,
    losersRaceLength: firebaseTournament?.losersRaceLength || firebaseTournament?.raceLength
      || firebaseTournament?.winnersRaceLength || raceLength || 5,
  }), [firebaseTournament?.winnersRaceLength, firebaseTournament?.losersRaceLength, firebaseTournament?.raceLength, raceLength]);

  // Both team formats abbreviate each partner to "First L." in the bracket/table
  // (full names still show in the score modal). Scotch Doubles was previously
  // missed by an `=== 'Doubles'` check, so its teams rendered full + truncated.
  const isDoublesFormat = firebaseTournament?.playerFormat === 'Doubles'
    || firebaseTournament?.playerFormat === 'Scotch Doubles';

  // Wrapper function that routes through admin Cloud Function when in admin mode
  const updateFirebaseTournament = useCallback(async (updates) => {
    if (isAdminMode && adminManagedTournamentId) {
      // Use admin Cloud Function for server-side authorization
      const { success, error } = await updateTournamentAdmin(adminManagedTournamentId, updates);
      if (error) {
        throw new Error(error);
      }
      return { success };
    } else {
      // Use regular update function (subject to Firestore rules)
      return await updateFirebaseTournamentDirect(updates);
    }
  }, [isAdminMode, adminManagedTournamentId, updateFirebaseTournamentDirect]);

  // Deep-merge variant for writes that target nested paths whose dynamic
  // segments contain dots — most importantly the recurring instance date keys
  // (e.g. "2026-05-26T19:00:00.000"). updateDoc dot-notation would split such
  // keys on every `.` and write to the wrong field; setDoc-with-merge avoids
  // that by passing the structure as a real nested object.
  const mergeFirebaseTournament = useCallback(async (nestedUpdates) => {
    if (isAdminMode && adminManagedTournamentId) {
      // Admin Cloud Function path doesn't have a merge variant yet — fall back
      // to the dot-notation updateDoc path. Admin-managed flows don't currently
      // hit the dotted-date-key write sites, so this stays safe.
      const { success, error } = await updateTournamentAdmin(adminManagedTournamentId, nestedUpdates);
      if (error) {
        throw new Error(error);
      }
      return { success };
    } else {
      return await mergeFirebaseTournamentDirect(nestedUpdates);
    }
  }, [isAdminMode, adminManagedTournamentId, mergeFirebaseTournamentDirect]);

  // The active occurrence's data. New schema: the subcollection document delivered
  // via `activeInstance`. Transitional read-only fallback: the legacy parent
  // `instances` map, so a pre-migration recurring tournament still renders until
  // it's recreated. Null when no occurrence is selected (navigator view).
  const currentInstance = selectedInstanceDate
    ? (activeInstance || firebaseTournament?.instances?.[selectedInstanceDate] || null)
    : null;

  // Keep the server-confirmed baseline ref fresh for the debounced sync closure.
  useEffect(() => { serverInstanceRef.current = serverInstance; }, [serverInstance]);

  const fileInputRef = useRef(null);
  const isInitialLoad = useRef(true);

  // Firebase sync management - only sync on specific actions
  const syncTimeoutRef = useRef(null);
  // Initialize to true if loading from /host/ URL to prevent sync during initial data load
  const isLoadingFromFirebase = useRef(initialUrlState.appMode === 'firebase');
  const lastSyncedData = useRef(null);
  // Snapshot of the bracket map last persisted to Firestore. Used to compute a
  // per-match diff so syncToFirebase only sends the changed fields rather than
  // the entire bracket. Reset whenever a tournament/instance is loaded.
  const lastSyncedBracketMapRef = useRef({});
  // Always-fresh mirror of the last SERVER-CONFIRMED occurrence snapshot, so the
  // debounced sync (whose closure intentionally omits instance state to avoid sync
  // loops) diffs against server truth rather than the optimistic cache.
  const serverInstanceRef = useRef(null);
  const pendingSyncRef = useRef(false); // Flag to trigger sync on next render
  // Holds the latest syncToFirebase closure so deferred one-shot writes (e.g. the
  // sourceMatch de-bloat heal scheduled from the load effect) operate on current state.
  const syncToFirebaseRef = useRef(null);
  // HYBRID SYNC ARCHITECTURE:
  // - When self-scoring is OFF: Director uses push-only mode, no real-time listener pulls from Firebase
  // - When self-scoring is ON: Real-time listener only pulls player score fields, not director fields
  // These guards are less critical with hybrid sync but kept for additional safety:
  const manualUpdateInProgressRef = useRef(false); // Block effects during performShuffleBracket
  const hasUnsyncedLocalChangesRef = useRef(false); // Track if there are local changes not yet synced

  // Sync status surfaced to the nav indicator so directors can see when writes
  // are in flight, offline, or have failed (critical on weak wifi where they
  // previously had no way to know whether their edits were saved).
  const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [hasPendingSync, setHasPendingSync] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const [hasSyncError, setHasSyncError] = useState(false);
  // True once the server has failed to acknowledge the occurrence's writes for
  // longer than SYNC_STUCK_MS — i.e. edits are stranded in the offline cache
  // behind a green LED. Drives the red indicator and the recovery watchdog.
  const [syncStuck, setSyncStuck] = useState(false);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Server has not yet acked this client's edits to the active occurrence doc.
  // This is the truthful "is it actually saved?" signal (vs. "did the write call
  // throw?", which it never does with an offline cache). Recurring-instance path
  // only — the failure we're fixing is always a live occurrence.
  const serverAckPending = appMode === 'firebase' && !!selectedInstanceDate && !!instanceMeta?.hasPendingWrites;

  const syncStatus = !isOnline
    ? 'offline'
    : (hasSyncError || syncStuck)
      ? 'error'
      : (hasPendingSync || serverAckPending)
        ? 'syncing'
        : 'synced';

  // A non-technical director will miss the LED dot, so surface a full-width,
  // can't-miss banner whenever edits are genuinely not reaching the server:
  // the write channel is wedged (syncStuck), or we're offline with edits still
  // pending. This is the highest-leverage mitigation — no code can manufacture
  // connectivity, so the human needs to know to fix it (or reload to recover).
  const showSyncAlert = appMode === 'firebase' && (syncStuck || (!isOnline && serverAckPending));

  // Lossless recovery: flush the latest state into Firestore's IndexedDB write
  // queue, then reload. A reload re-instantiates the client and cleanly re-drains
  // that persisted queue once the connection/token is healthy — and nothing is
  // lost, because queued writes survive the reload in IndexedDB.
  const handleSyncReload = useCallback(() => {
    try { if (syncToFirebaseRef.current) syncToFirebaseRef.current(true, true); } catch (e) { /* enqueue best-effort */ }
    logSyncDiag('manual-reload', { online: typeof navigator !== 'undefined' ? navigator.onLine : null });
    setTimeout(() => window.location.reload(), 400);
  }, []);

  // Fire an out-of-band diagnostic beacon (survives tab close; independent of the
  // wedged Firestore stream) so the next real wedge yields ground truth on the
  // cause (network-down vs auth/stream vs code) instead of dying with the tab.
  const sendSyncBeacon = useCallback((reason) => {
    if (appMode !== 'firebase' || !selectedTournament?.id) return;
    try {
      const payload = {
        reason,
        v: APP_SYNC_VERSION,
        t: new Date().toISOString(),
        tournamentId: selectedTournament.id,
        instanceDate: selectedInstanceDate || null,
        onLine: typeof navigator !== 'undefined' ? navigator.onLine : null,
        pendingWrites: !!instanceMeta?.hasPendingWrites,
        fromCache: !!instanceMeta?.fromCache,
        hasSyncError,
        syncStuck,
        tokenExpires: auth.currentUser?.stsTokenManager?.expirationTime || null,
        diag: (typeof window !== 'undefined' && Array.isArray(window.__syncDiag))
          ? window.__syncDiag.slice(-40) : [],
      };
      const blob = new Blob([JSON.stringify(payload)], { type: 'text/plain' });
      if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
        navigator.sendBeacon(SYNC_DIAG_URL, blob);
      }
    } catch (e) { /* best effort — a beacon must never disrupt the app */ }
  }, [appMode, selectedTournament?.id, selectedInstanceDate, instanceMeta, hasSyncError, syncStuck]);
  const sendSyncBeaconRef = useRef(sendSyncBeacon);
  useEffect(() => { sendSyncBeaconRef.current = sendSyncBeacon; }, [sendSyncBeacon]);

  // Fire ONCE per stuck episode (not on every metadata tick while stuck).
  const stuckBeaconSentRef = useRef(false);
  useEffect(() => {
    if (syncStuck && !stuckBeaconSentRef.current) {
      stuckBeaconSentRef.current = true;
      sendSyncBeaconRef.current('stuck');
    } else if (!syncStuck) {
      stuckBeaconSentRef.current = false;
    }
  }, [syncStuck]);

  // Fire on tab close/hide if edits are still unsynced — captures "director left
  // with N unsynced writes at time T," the data-loss moment.
  useEffect(() => {
    const onHide = () => {
      if (serverAckPending || hasUnsyncedLocalChangesRef.current) {
        sendSyncBeaconRef.current('pagehide');
      }
    };
    window.addEventListener('pagehide', onHide);
    return () => window.removeEventListener('pagehide', onHide);
  }, [serverAckPending]);

  // Check URL for public tournament sharing and player scoring
  useEffect(() => {
    const urlPath = window.location.pathname;

    // Check for tournament share URL
    const shareUrlMatch = urlPath.match(/^\/tournament\/([a-zA-Z0-9]+)$/);
    if (shareUrlMatch) {
      const shareUrl = shareUrlMatch[1];
      setPublicShareUrl(shareUrl);
      setAppMode('public');
      return;
    }

    // Check for player scoring URL - new format: /score/{tournamentId}/{token}
    const scoreUrlMatchNew = urlPath.match(/^\/score\/([a-zA-Z0-9]+)\/([a-zA-Z0-9-]+)$/);
    if (scoreUrlMatchNew) {
      const tournamentId = scoreUrlMatchNew[1];
      const token = scoreUrlMatchNew[2];
      setScoringTournamentId(tournamentId);
      setScoringToken(token);
      setAppMode('scoring');
      return;
    }

    // Check for player scoring URL - old format: /score/{token} (backwards compatibility)
    const scoreUrlMatchOld = urlPath.match(/^\/score\/([a-zA-Z0-9-]+)$/);
    if (scoreUrlMatchOld) {
      const token = scoreUrlMatchOld[1];
      setScoringToken(token);
      setAppMode('scoring');
      return;
    }

    // Check for streaming overlay URL - format: /overlay/{tournamentId}/{tableNumber}
    const overlayUrlMatch = urlPath.match(/^\/overlay\/([a-zA-Z0-9]+)(?:\/(\d+))?$/);
    if (overlayUrlMatch) {
      const tournamentId = overlayUrlMatch[1];
      const tableNum = overlayUrlMatch[2] ? parseInt(overlayUrlMatch[2], 10) : 1;
      setOverlayTournamentId(tournamentId);
      setOverlayTableNumber(tableNum);
      setAppMode('overlay');
      return;
    }

    // Check for privacy page URL
    if (urlPath === '/privacy') {
      setAppMode('privacy');
      return;
    }

    // Check for terms page URL
    if (urlPath === '/terms') {
      setAppMode('terms');
      return;
    }

    // Check for contact page URL
    if (urlPath === '/contact') {
      setAppMode('contact');
      return;
    }

    // Check for help page URL
    if (urlPath === '/help') {
      setAppMode('help');
      return;
    }

    // Check for local tournament URL
    if (urlPath === '/local') {
      setAppMode('local');
      return;
    }

    // Check for finder URL
    if (urlPath === '/find') {
      setAppMode('finder');
      return;
    }

    // Check for leaderboard URL
    if (urlPath === '/leaderboard') {
      setAppMode('leaderboard');
      return;
    }

    // Check for player profile share URL - /player/{uid}
    const playerUrlMatch = urlPath.match(/^\/player\/([a-zA-Z0-9_-]+)$/);
    if (playerUrlMatch) {
      setViewPlayerId(playerUrlMatch[1]);
      setAppMode('player');
      return;
    }

    // Check for dashboard URL
    if (urlPath === '/dashboard') {
      setAppMode('dashboard');
      return;
    }

    // Check for admin URL
    if (urlPath === '/admin') {
      setAppMode('admin');
      return;
    }

    // Check for host tournament URL - format: /host/{tournamentId} or /host/{tournamentId}/{instanceDate}
    const hostUrlMatch = urlPath.match(/^\/host\/([a-zA-Z0-9]+)(?:\/(\d{4}-\d{2}-\d{2}))?$/);
    if (hostUrlMatch) {
      const tournamentId = hostUrlMatch[1];
      const instanceDate = hostUrlMatch[2] || null;
      // Set loading flag - data will be loaded by useEffect when firebaseTournament arrives
      isLoadingFromFirebase.current = true;
      setSelectedTournament({ id: tournamentId });
      setSelectedInstanceDate(instanceDate);
      setAppMode('firebase');
      return;
    }
  }, []);

  // Handle browser back/forward button navigation
  useEffect(() => {
    const handlePopState = () => {
      const urlPath = window.location.pathname;

      // Check for tournament share URL
      const shareUrlMatch = urlPath.match(/^\/tournament\/([a-zA-Z0-9]+)$/);
      if (shareUrlMatch) {
        setPublicShareUrl(shareUrlMatch[1]);
        setAppMode('public');
        return;
      }

      // Check for player scoring URL - new format
      const scoreUrlMatchNew = urlPath.match(/^\/score\/([a-zA-Z0-9]+)\/([a-zA-Z0-9-]+)$/);
      if (scoreUrlMatchNew) {
        setScoringTournamentId(scoreUrlMatchNew[1]);
        setScoringToken(scoreUrlMatchNew[2]);
        setAppMode('scoring');
        return;
      }

      // Check for player scoring URL - old format
      const scoreUrlMatchOld = urlPath.match(/^\/score\/([a-zA-Z0-9-]+)$/);
      if (scoreUrlMatchOld) {
        setScoringToken(scoreUrlMatchOld[1]);
        setAppMode('scoring');
        return;
      }

      // Check for streaming overlay URL
      const overlayUrlMatch = urlPath.match(/^\/overlay\/([a-zA-Z0-9]+)(?:\/(\d+))?$/);
      if (overlayUrlMatch) {
        setOverlayTournamentId(overlayUrlMatch[1]);
        setOverlayTableNumber(overlayUrlMatch[2] ? parseInt(overlayUrlMatch[2], 10) : 1);
        setAppMode('overlay');
        return;
      }

      // Check for static pages
      if (urlPath === '/privacy') {
        setAppMode('privacy');
        return;
      }
      if (urlPath === '/terms') {
        setAppMode('terms');
        return;
      }
      if (urlPath === '/contact') {
        setAppMode('contact');
        return;
      }
      if (urlPath === '/help') {
        setAppMode('help');
        return;
      }
      if (urlPath === '/find') {
        setAppMode('finder');
        return;
      }
      if (urlPath === '/leaderboard') {
        setAppMode('leaderboard');
        return;
      }
      const playerUrlMatchPop = urlPath.match(/^\/player\/([a-zA-Z0-9_-]+)$/);
      if (playerUrlMatchPop) {
        setViewPlayerId(playerUrlMatchPop[1]);
        setAppMode('player');
        return;
      }
      if (urlPath === '/dashboard') {
        setAppMode('dashboard');
        return;
      }
      if (urlPath === '/admin') {
        setAppMode('admin');
        return;
      }
      if (urlPath === '/local') {
        setAppMode('local');
        return;
      }

      // Check for host tournament URL - format: /host/{tournamentId} or /host/{tournamentId}/{instanceDate}
      const hostUrlMatch = urlPath.match(/^\/host\/([a-zA-Z0-9]+)(?:\/(\d{4}-\d{2}-\d{2}))?$/);
      if (hostUrlMatch) {
        const tournamentId = hostUrlMatch[1];
        const instanceDate = hostUrlMatch[2] || null;
        isLoadingFromFirebase.current = true;
        setSelectedTournament({ id: tournamentId });
        setSelectedInstanceDate(instanceDate);
        setAppMode('firebase');
        return;
      }

      // Default to landing page
      if (urlPath === '/' || urlPath === '') {
        setAppMode('landing');
        setSelectedTournament(null);
        setSelectedInstanceDate(null);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Reset the openFinderOnDashboard flag after dashboard is shown
  useEffect(() => {
    if (appMode === 'dashboard' && openFinderOnDashboard) {
      // Wait a bit to ensure the Dashboard component has mounted
      const timeout = setTimeout(() => {
        setOpenFinderOnDashboard(false);
      }, 500);
      return () => clearTimeout(timeout);
    }
  }, [appMode, openFinderOnDashboard]);

  // Reset the openCreateModalOnDashboard flag after dashboard is shown
  useEffect(() => {
    if (appMode === 'dashboard' && openCreateModalOnDashboard) {
      // Wait a bit to ensure the Dashboard component has mounted
      const timeout = setTimeout(() => {
        setOpenCreateModalOnDashboard(false);
      }, 500);
      return () => clearTimeout(timeout);
    }
  }, [appMode, openCreateModalOnDashboard]);

  // Load Firebase tournament data into local state (only when switching to firebase mode)
  const lastFirebaseLoad = useRef(null);
  const lastInstanceLoad = useRef(null);
  const lastInstanceDataHash = useRef(null);
  const justSyncedRef = useRef(false); // Prevents reload immediately after our own sync
  // Monotonic write version. Every Firestore write stamps clientWriteVersion onto the
  // payload; incoming snapshots whose clientWriteVersion is older than what we last
  // wrote are rejected so a stale snapshot (or an interleaved player live-score write)
  // can't overwrite director edits, regardless of network timing.
  const localWriteVersionRef = useRef(0);
  const expectedAckVersionRef = useRef(0);
  // Guard parent-doc writes during a live instance so mid-match score ticks (which
  // don't change the summary or settings) don't re-deliver the parent snapshot to
  // every spectator. Keyed by instance date so switching occurrences resets them.
  const lastInstanceSummaryRef = useRef({ key: null, json: null });
  const lastTournamentSettingsRef = useRef(null);
  useEffect(() => {
    // Skip reload if manual update (performShuffleBracket) is in progress
    // This prevents race condition where loading old Firebase data overwrites new bracket
    if (manualUpdateInProgressRef.current) {
      return;
    }

    // Skip reload if we just synced our own changes to Firebase
    // This prevents the race condition where Firebase listener fires after our sync
    // and overwrites newer local changes
    if (justSyncedRef.current) {
      justSyncedRef.current = false;
      return;
    }

    // Reject any snapshot whose clientWriteVersion is older than the last write we
    // initiated. This is the bulletproof guard against stale snapshots overwriting
    // a director's edit on slow networks — covers cases the timing-based flags miss
    // (e.g., a player live-score write whose echo arrives after our manual flag clears).
    if (firebaseTournament) {
      const incomingVersion = selectedInstanceDate
        ? currentInstance?.clientWriteVersion
        : firebaseTournament.clientWriteVersion;
      if (typeof incomingVersion === 'number' && incomingVersion < expectedAckVersionRef.current) {
        return;
      }
    }

    // In the subcollection schema the occurrence arrives via its own subscription.
    // If one is selected but hasn't loaded yet, wait — never fall back to the root
    // bracket (that would render the wrong tournament in an instance view).
    if (selectedInstanceDate && !currentInstance) {
      return;
    }

    // Calculate a hash of instance data to detect meaningful changes
    // NOTE: We intentionally exclude lastUpdated to avoid reload loops
    // lastUpdated changes on every sync, which would cause constant reloads
    const instanceData = selectedInstanceDate && currentInstance;
    const instanceBracketLength = instanceData
      ? (instanceData.matchOrder?.length
          ?? (instanceData.bracketMap ? Object.keys(instanceData.bracketMap).length : 0)
          ?? instanceData.bracket?.length
          ?? 0)
      : 0;
    const currentDataHash = instanceData ? JSON.stringify({
      bracketLength: instanceBracketLength,
      playerCount: instanceData.players?.length || 0,
      textAlertsActivated: instanceData.textAlertsActivated || false
    }) : null;

    const needsLoad = appMode === 'firebase' && firebaseTournament &&
        (lastFirebaseLoad.current !== firebaseTournament.id ||
         lastInstanceLoad.current !== selectedInstanceDate ||
         (selectedInstanceDate && lastInstanceDataHash.current !== currentDataHash));

    if (needsLoad) {
      // Set flag to prevent sync during load
      isLoadingFromFirebase.current = true;

      // Set when a loaded main-tournament document still carries the recursive
      // `sourceMatch` build artifact, so we can de-bloat it with one forced save.
      let needsBloatHeal = false;

      // Helper function to process bracket with winner propagation
      const processLoadedBracket = (bracketData, format) => {
        if (!bracketData || bracketData.length === 0) return bracketData;

        // Apply winner propagation to ensure bracket state is consistent
        const tournamentManager = createTournamentManager(format);
        let processedBracket = structuredClone(bracketData);
        let prevBracketState;
        let passes = 0;
        const maxPasses = 10;

        do {
          prevBracketState = getBracketStateHash(processedBracket);
          tournamentManager.finalizeByeMatches(processedBracket);
          tournamentManager.propagateWinners(processedBracket);
          passes++;
        } while (getBracketStateHash(processedBracket) !== prevBracketState && passes < maxPasses);

        // De-bloat any legacy document that still carries the recursive `sourceMatch`
        // build artifact on p1/p2, so working state (and the next save) stays lean.
        return stripSourceMatch(processedBracket);
      };

      // Load instance-specific data if an instance is selected
      if (selectedInstanceDate && currentInstance) {
        // Load instance data (works for past, present, and future instances)
        const instanceData = currentInstance;
        const instanceFormat = instanceData.format || firebaseTournament.format || TOURNAMENT_FORMATS.DOUBLE_ELIMINATION;
        const instancePlayers = instanceData.players?.join('\n') || '';
        setPlayersText(instancePlayers);
        setLastPlayerList(instancePlayers); // Keep lastPlayerList in sync with loaded tournament
        // Apply winner propagation when loading bracket to ensure consistency
        const processedBracket = processLoadedBracket(readBracketAsArray(instanceData), instanceFormat);
        setBracket(processedBracket);
        setPayoutAmounts(instanceData.payoutAmounts || ['']);
        setNumPayoutPlaces(instanceData.numPayoutPlaces || 3);
        setTournamentFormat(instanceFormat);
        setPlayerPayments(instanceData.playerPayments || {});
        setPlayerPhones(instanceData.playerPhones || {});
        setPlayerUserIds(instanceData.playerUserIds || {});
        // Load text alerts state from instance data
        setTextAlertsActivated(instanceData.textAlertsActivated || false);
      } else {
        // Load main tournament data (for non-recurring or when no instance selected)
        const mainFormat = firebaseTournament.format || TOURNAMENT_FORMATS.DOUBLE_ELIMINATION;
        const mainPlayers = firebaseTournament.players?.join('\n') || '';
        setPlayersText(mainPlayers);
        setLastPlayerList(mainPlayers); // Keep lastPlayerList in sync with loaded tournament
        // Apply winner propagation when loading bracket to ensure consistency
        const rawMainBracket = readBracketAsArray(firebaseTournament);
        // Detect the recursive sourceMatch bloat in the stored doc before stripping,
        // so we can schedule a one-time clean re-save below (main path only — the
        // per-match field-path diff in syncToFirebase replaces p1/p2 wholesale,
        // which removes the nested artifact; recurring instances rely on repair tooling).
        needsBloatHeal = bracketHasSourceMatchBloat(rawMainBracket);
        const processedBracket = processLoadedBracket(rawMainBracket, mainFormat);
        setBracket(processedBracket);
        setPayoutAmounts(firebaseTournament.payoutAmounts || ['']);
        setNumPayoutPlaces(firebaseTournament.numPayoutPlaces || 3);
        setTournamentFormat(mainFormat);
        setPlayerPayments(firebaseTournament.playerPayments || {});
        setPlayerPhones(firebaseTournament.playerPhones || {});
        setPlayerUserIds(firebaseTournament.playerUserIds || {});
        // Load text alerts state for non-recurring tournaments
        setTextAlertsActivated(firebaseTournament.textAlertsActivated || false);
      }

      lastFirebaseLoad.current = firebaseTournament.id;
      lastInstanceLoad.current = selectedInstanceDate;
      lastInstanceDataHash.current = currentDataHash;

      if (firebaseTournament.tournamentSettings?.customTableNumbers) {
        // Only update if Firebase has a saved value - otherwise keep localStorage value
        setCustomTableNumbers(firebaseTournament.tournamentSettings.customTableNumbers);
      }

      // For non-SMS tournaments, reset autoReassignTables to false ONLY on initial tournament load
      // (not on subsequent syncs). This prevents the checkbox from appearing checked but not functioning.
      const isInitialTournamentLoad = lastFirebaseLoad.current !== firebaseTournament.id;
      if (isInitialTournamentLoad && !firebaseTournament.selfScoringEnabled) {
        setAutoReassignTables(false);
      }

      // Recalculate node positions
      const currentBracket = selectedInstanceDate && currentInstance
        ? readBracketAsArray(currentInstance)
        : readBracketAsArray(firebaseTournament);

      if (currentBracket?.length > 0) {
        const currentPlayers = selectedInstanceDate && currentInstance?.players
          ? currentInstance.players
          : firebaseTournament.players;
        const playerCount = currentPlayers?.length || 0;
        const rounds = Math.ceil(Math.log2(Math.max(1, playerCount)));
        const positions = getBracketNodePositions(currentBracket, rounds);
        setNodePositions(positions);
      }

      // Store the loaded data as last synced (instance-aware)
      const loadedPlayers = selectedInstanceDate && currentInstance?.players
        ? currentInstance.players
        : firebaseTournament.players || [];
      const loadedBracket = selectedInstanceDate && currentInstance
        ? readBracketAsArray(currentInstance)
        : readBracketAsArray(firebaseTournament);
      const loadedPayoutAmounts = selectedInstanceDate && currentInstance?.payoutAmounts
        ? currentInstance.payoutAmounts
        : firebaseTournament.payoutAmounts || [''];
      const loadedNumPayoutPlaces = selectedInstanceDate && currentInstance?.numPayoutPlaces
        ? currentInstance.numPayoutPlaces
        : firebaseTournament.numPayoutPlaces || 3;

      // Capture the canonical bracketMap snapshot for diff-based syncing.
      // Prefer the stored bracketMap when present (post-migration). Otherwise
      // derive a map from the legacy bracket array so diffs still work during
      // the transition.
      const loadedSource = selectedInstanceDate && currentInstance
        ? currentInstance
        : firebaseTournament;
      if (loadedSource?.bracketMap && typeof loadedSource.bracketMap === 'object') {
        lastSyncedBracketMapRef.current = loadedSource.bracketMap;
      } else {
        lastSyncedBracketMapRef.current = bracketArrayToMap(loadedBracket).bracketMap;
      }

      lastSyncedData.current = JSON.stringify({
        players: loadedPlayers.join('\n'),
        bracket: loadedBracket,
        payoutAmounts: loadedPayoutAmounts,
        numPayoutPlaces: loadedNumPayoutPlaces,
      });

      // Clear the loading flag after a short delay
      setTimeout(() => {
        isLoadingFromFirebase.current = false;
      }, 100);

      // One-time de-bloat: if the stored document still had the recursive sourceMatch
      // artifact, force a single clean save once loading settles. The diff baseline
      // (lastSyncedBracketMapRef) still points at the bloated stored map, so this write
      // emits lean {name, ref} participants for every affected match and permanently
      // shrinks the document. Runs after the loading flag clears (100ms) and after the
      // stripped bracket has re-rendered (syncToFirebaseRef now closes over clean state).
      if (needsBloatHeal) {
        setTimeout(() => {
          if (syncToFirebaseRef.current) {
            syncToFirebaseRef.current(true, true); // force + immediate
          }
        }, 400);
      }

    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firebaseTournament, activeInstance, appMode, selectedInstanceDate, setBracket, setCustomTableNumbers, setAutoReassignTables, setNodePositions, setNumPayoutPlaces, setPayoutAmounts, setTournamentFormat, setPlayerPayments, setPlayerPhones]);

  // Track if real-time sync made changes that need to be synced back to Firebase
  const realtimeSyncMadeChangesRef = useRef(false);

  // Sync real-time bracket updates from Firebase (for player self-scoring changes)
  // This handles cases where a player starts/finishes a match and we need to update locally
  useEffect(() => {
    // Only sync when we're in firebase mode and have a tournament loaded
    // Skip if manual update (performShuffleBracket) is in progress to prevent race conditions
    // Skip if there are unsynced local changes to prevent overwriting director's edits
    if (appMode !== 'firebase' || !firebaseTournament || isLoadingFromFirebase.current || manualUpdateInProgressRef.current || hasUnsyncedLocalChangesRef.current) {
      return;
    }

    // Reject snapshots older than our last write (same logic as the reload effect above)
    {
      const incomingVersion = selectedInstanceDate
        ? currentInstance?.clientWriteVersion
        : firebaseTournament.clientWriteVersion;
      if (typeof incomingVersion === 'number' && incomingVersion < expectedAckVersionRef.current) {
        return;
      }
    }

    // HYBRID SYNC: When self-scoring is disabled, director uses push-only mode
    // No need to pull from Firebase since director is the only one making changes
    // This eliminates race conditions and simplifies the sync logic
    const selfScoringEnabled = firebaseTournament?.selfScoringEnabled || false;
    if (!selfScoringEnabled) {
      return; // Skip real-time listener - director uses push-only
    }

    // In the subcollection schema, wait for the occurrence doc before syncing.
    if (selectedInstanceDate && !currentInstance) {
      return;
    }

    // Get the current Firebase bracket
    const firebaseBracket = selectedInstanceDate && currentInstance
      ? readBracketAsArray(currentInstance)
      : readBracketAsArray(firebaseTournament);

    if (!firebaseBracket || firebaseBracket.length === 0) {
      return;
    }

    // Sync specific fields from Firebase that can be updated by players
    // (started, finished, selfScored, score1, score2, winner, actualStartTime, actualEndTime)
    setBracket(prevBracket => {
      if (!prevBracket || prevBracket.length !== firebaseBracket.length) {
        return prevBracket; // Don't sync if bracket structure is different
      }

      let hasChanges = false;
      let hasNewlyFinishedMatch = false;

      const updatedBracket = prevBracket.map((match, index) => {
        const firebaseMatch = firebaseBracket[index];
        if (!firebaseMatch) return match;

        // If the local match is already finished, treat local as authoritative
        // and skip the merge entirely. Finalization is a one-way transition —
        // any subsequent listener echo (especially one carrying preserved
        // player fields from before our write) must not be allowed to revert
        // `finished` to false or replace the director's final score/winner
        // with stale values. This was the source of the complete→playing→
        // complete flicker after switching syncToFirebase to setDoc(merge).
        if (match.finished) {
          return match;
        }

        // Check if player-controlled fields have changed in Firebase
        // NOTE: 'started' and 'actualStartTime' are director-controlled (director clicks "Start Match")
        // Players only control score-related fields after the match is started
        const playerFields = ['finished', 'selfScored', 'score1', 'score2', 'winner', 'actualEndTime', 'scoreRetryCount', 'scoreConflict'];
        const needsUpdate = playerFields.some(field => {
          return firebaseMatch[field] !== undefined && firebaseMatch[field] !== match[field];
        });

        if (needsUpdate) {
          hasChanges = true;

          // Check if this match is newly finished (was not finished before, now is finished)
          if (firebaseMatch.finished && firebaseMatch.winner) {
            hasNewlyFinishedMatch = true;
          }

          // Merge Firebase player fields into local match
          // NOTE: 'started' and 'actualStartTime' are NOT merged - those are director-controlled
          return {
            ...match,
            finished: firebaseMatch.finished,
            selfScored: firebaseMatch.selfScored,
            score1: firebaseMatch.score1,
            score2: firebaseMatch.score2,
            winner: firebaseMatch.winner,
            actualEndTime: firebaseMatch.actualEndTime,
            scoreRetryCount: firebaseMatch.scoreRetryCount,
            scoreConflict: firebaseMatch.scoreConflict
          };
        }

        return match;
      });

      if (!hasChanges) {
        return prevBracket;
      }

      // If a match was newly finished, apply tournament logic to propagate winners
      if (hasNewlyFinishedMatch) {
        const tournamentManager = createTournamentManager(tournamentFormat);

        // Find the table that was freed (from the match that just finished)
        const finishedMatch = updatedBracket.find((m, i) => {
          const prevMatch = prevBracket[i];
          return m.finished && (!prevMatch || !prevMatch.finished) && m.table;
        });
        const freedTable = finishedMatch?.table;

        // Apply multiple passes to ensure all propagations are processed
        let processedBracket = structuredClone(updatedBracket);
        let prevBracketState;
        let passes = 0;
        const maxPasses = 10;

        do {
          prevBracketState = getBracketStateHash(processedBracket);
          tournamentManager.finalizeByeMatches(processedBracket);
          tournamentManager.propagateWinners(processedBracket);
          passes++;
        } while (getBracketStateHash(processedBracket) !== prevBracketState && passes < maxPasses);

        // If auto-reassignment is enabled and there was a freed table, assign it to next available match
        if (autoReassignTables && freedTable) {
          processedBracket = assignFreedTable(processedBracket, freedTable);
        }

        // Additionally, check if there are newly ready matches that need tables
        // (beyond just the freed table scenario - there might be other available tables)
        if (autoReassignTables && customTableNumbers?.length > 0) {
          const tableSettings = { customTableNumbers };
          const unusedTables = getUnusedTables(tableSettings, processedBracket);

          if (unusedTables.length > 0) {
            // Find matches that just became ready and need tables
            const matchesNeedingTables = processedBracket.filter(match => {
              const player1Name = match.player1 || match.p1?.name;
              const player2Name = match.player2 || match.p2?.name;
              const hasPlayer1 = player1Name && player1Name !== 'BYE' && player1Name !== 'TBD' && !player1Name.includes('Winner of') && !player1Name.includes('Loser of');
              const hasPlayer2 = player2Name && player2Name !== 'BYE' && player2Name !== 'TBD' && !player2Name.includes('Winner of') && !player2Name.includes('Loser of');
              return hasPlayer1 && hasPlayer2 && !match.finished && !match.table;
            });

            // Assign available tables to ready matches (prioritize by round and match number)
            matchesNeedingTables.sort((a, b) => {
              if (a.round !== b.round) return a.round - b.round;
              return (a.matchNum || 0) - (b.matchNum || 0);
            });

            let tableIndex = 0;
            processedBracket = processedBracket.map(match => {
              if (tableIndex >= unusedTables.length) return match;

              const matchNeedsTable = matchesNeedingTables.find(m => m.id === match.id);
              if (matchNeedsTable) {
                const assignedTable = unusedTables[tableIndex++];
                return { ...match, table: assignedTable };
              }
              return match;
            });
          }
        }

        // Check for newly ready matches that need tokens
        // A match is "ready" when both players are assigned and neither is BYE/TBD/Winner of/Loser of
        const selfScoringEnabled = firebaseTournament?.selfScoringEnabled || false;
        const instanceData = currentInstance;

        if (selfScoringEnabled && firebaseTournament?.id) {
          // Generate tokens for ready matches (SMS is sent only when director clicks "Start Match")
          processedBracket = processedBracket.map((match, index) => {
            const player1Name = match.player1 || match.p1?.name;
            const player2Name = match.player2 || match.p2?.name;
            const hasPlayer1 = player1Name && player1Name !== 'BYE' && player1Name !== 'TBD' && !player1Name.includes('Winner of') && !player1Name.includes('Loser of');
            const hasPlayer2 = player2Name && player2Name !== 'BYE' && player2Name !== 'TBD' && !player2Name.includes('Winner of') && !player2Name.includes('Loser of');

            // Get phone data for both players
            const currentPlayerPhones = instanceData?.playerPhones || firebaseTournament?.playerPhones || {};
            const p1PhoneData = currentPlayerPhones[player1Name];
            const p2PhoneData = currentPlayerPhones[player2Name];

            // Match just became ready (both players assigned, not finished, no tokens yet)
            if (hasPlayer1 && hasPlayer2 && !match.finished && !match.p1Token) {
              // Generate tokens for this newly ready match
              const tokens = generateMatchTokens(firebaseTournament.id, index);

              return {
                ...match,
                p1Token: tokens.p1Token,
                p2Token: tokens.p2Token,
                tokenCreatedAt: tokens.tokenCreatedAt,
                p1Phone: p1PhoneData?.phone || null,
                p2Phone: p2PhoneData?.phone || null,
                p1PhoneMasked: p1PhoneData?.phoneMasked || null,
                p2PhoneMasked: p2PhoneData?.phoneMasked || null
              };
            }

            // Update phone info if players advanced to a match that already has tokens
            if (hasPlayer1 && hasPlayer2 && !match.finished && match.p1Token) {
              const needsPhoneUpdate =
                (p1PhoneData?.phone && !match.p1Phone) ||
                (p2PhoneData?.phone && !match.p2Phone);

              if (needsPhoneUpdate) {
                return {
                  ...match,
                  p1Phone: p1PhoneData?.phone || match.p1Phone || null,
                  p2Phone: p2PhoneData?.phone || match.p2Phone || null,
                  p1PhoneMasked: p1PhoneData?.phoneMasked || match.p1PhoneMasked || null,
                  p2PhoneMasked: p2PhoneData?.phoneMasked || match.p2PhoneMasked || null
                };
              }
            }

            return match;
          });
        }

        // Mark that we made changes that need to be synced back to Firebase
        // (winner propagation, table assignments, token generation, SMS flags)
        realtimeSyncMadeChangesRef.current = true;

        return processedBracket;
      }

      return updatedBracket;
    });
  }, [firebaseTournament, appMode, selectedInstanceDate, setBracket, tournamentFormat, autoReassignTables, customTableNumbers]);

  // Effect to sync changes made by real-time processing back to Firebase
  useEffect(() => {
    if (realtimeSyncMadeChangesRef.current && appMode === 'firebase' && selectedTournament?.id) {
      realtimeSyncMadeChangesRef.current = false;
      pendingSyncRef.current = true;
    }
  }, [bracket, appMode, selectedTournament?.id]);

  // Player-stats recording has moved SERVER-SIDE to the `recordTournamentStats`
  // Cloud Function (functions/playerStats.js). It fires when a tournament/instance
  // is marked completed and credits every linked player regardless of whose
  // session is open, with persisted idempotency. The old client-side recorder was
  // removed here to avoid double-counting (it only ran in the owner's browser and
  // had no persistent dedup).

  // Manual sync function - only call this when specific actions occur
  // immediate = true bypasses the debounce (used when navigating away)
  const syncToFirebase = useCallback(async (force = false, immediate = false) => {
    if (appMode !== 'firebase' || !selectedTournament?.id) {
      return;
    }

    // Don't sync if we're currently loading from Firebase
    if (isLoadingFromFirebase.current) {
      return;
    }

    // Check if data has actually changed (unless force is true)
    const currentData = JSON.stringify({
      players: playersText,
      bracket,
      payoutAmounts,
      numPayoutPlaces,
      playerPayments,
      playerPhones,
      playerUserIds, // Include playerUserIds in change detection
    });

    if (!force && currentData === lastSyncedData.current) {
      return;
    }

    // Mark that we have local changes pending sync
    hasUnsyncedLocalChangesRef.current = true;

    // Clear existing timeout
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
    }

    // The actual sync logic
    const performSync = async () => {
      // Set flag BEFORE the Firebase write to prevent the listener from
      // triggering a reload while we're still syncing
      justSyncedRef.current = true;
      // Surface "saving…" to the nav sync indicator
      setHasPendingSync(true);
      setHasSyncError(false);

      try {
        // If viewing an instance, save to that instance's data
        if (selectedInstanceDate) {
          // Determine tournament status
          const hasStarted = bracket.length > 0;
          const allMatchesFinished = bracket.length > 0 && bracket.every(m =>
            m.finished || m.p1?.name?.includes('Winner of') || m.p2?.name?.includes('Winner of') || m.p1?.name === 'BYE' || m.p2?.name === 'BYE'
          );
          const tournamentComplete = allMatchesFinished && bracket.some(m =>
            m.finished && !m.p1?.name?.includes('Winner of') && !m.p2?.name?.includes('Winner of')
          );

          // Find the winner and placements using the tournament manager's getStandings method
          let winner = null;
          let secondPlace = null;
          let thirdPlace = null;
          let fourthPlace = null;
          let finalStandings = null;
          if (tournamentComplete && bracket.length > 0) {
            try {
              const manager = createTournamentManager(tournamentFormat);
              const standings = manager.getStandings(bracket);
              // Convert nested array to flat array of objects for Firebase compatibility
              // Firebase doesn't support nested arrays, so we flatten: [[player1], [player2]] -> [{placement: 1, name: 'player1'}, ...]
              if (standings && standings.length > 0) {
                finalStandings = [];
                standings.forEach((playersAtPlace, index) => {
                  if (Array.isArray(playersAtPlace)) {
                    playersAtPlace.forEach(playerName => {
                      finalStandings.push({
                        placement: index + 1,
                        name: playerName
                      });
                    });
                  }
                });
              }
              // Winner is the first place (first entry in standings)
              if (standings && standings.length > 0 && standings[0].length > 0) {
                winner = standings[0][0]; // First player in first place
              }
              // Second place
              if (standings && standings.length > 1 && standings[1].length > 0) {
                secondPlace = standings[1][0];
              }
              // Third place
              if (standings && standings.length > 2 && standings[2].length > 0) {
                thirdPlace = standings[2][0];
              }
              // Fourth place
              if (standings && standings.length > 3 && standings[3].length > 0) {
                fourthPlace = standings[3][0];
              }
            } catch (error) {
              console.error('Error getting standings:', error);
              // Fallback: try to find the winner from the final match
              const finalMatch = bracket.find(m =>
                m.finished && m.winner && !m.winner.includes('Winner of') && m.winner !== 'BYE'
              );
              winner = finalMatch ? finalMatch.winner : null;
            }
          }

          // Calculate tournament end time from the last finished match
          let tournamentEndTime = null;
          if (tournamentComplete && bracket.length > 0) {
            const finishedMatches = bracket.filter(m => m.finished && m.actualEndTime);
            if (finishedMatches.length > 0) {
              tournamentEndTime = Math.max(...finishedMatches.map(m => m.actualEndTime));
            }
          }

          // Calculate total prize pool from payout amounts
          const totalPrizePool = payoutAmounts && payoutAmounts.length > 0
            ? payoutAmounts.reduce((sum, amount) => sum + (parseFloat(amount) || 0), 0)
            : 0;

          // The bracket is the source of truth for who's playing once it's built —
          // derive the saved players list from it so it can't drift from bracketMap
          // (e.g. a late player swapped into a BYE seat). Fall back to the text list
          // only before a bracket exists.
          const players = bracket.length > 0
            ? getBracketPlayers(bracket)
            : playersText.split('\n').filter(Boolean);

          // Preserve existing canceled status if it exists
          const existingInstance = currentInstance;
          // Whether the occurrence's OWN subcollection document already exists.
          // (currentInstance may be a transitional legacy-map read with no doc yet.)
          const instanceDocExists = !!activeInstance;

          // Bump monotonic write version so the listener can reject stale snapshots
          localWriteVersionRef.current += 1;
          expectedAckVersionRef.current = localWriteVersionRef.current;

          const { bracketMap: currentBracketMap, matchOrder: currentMatchOrder } = bracketArrayToMap(bracket);

          // Metadata leaves for the occurrence document. The heavy bracketMap is
          // sent as a per-match field diff by writeInstanceBracketDiff so only
          // changed matches upload — critical on poor connections.
          const instanceMetadata = {
            players,
            playerCount: players.length,
            payoutAmounts,
            numPayoutPlaces,
            format: tournamentFormat,
            completed: tournamentComplete,
            inProgress: hasStarted && !tournamentComplete,
            winner,
            secondPlace,
            thirdPlace,
            fourthPlace,
            finalStandings,
            tournamentEndTime,
            totalPrizePool,
            canceled: existingInstance?.canceled || false,
            cancelReason: existingInstance?.cancelReason || null,
            playerPayments,
            playerPhones,
            playerUserIds,
            textAlertsActivated,
            clientWriteVersion: localWriteVersionRef.current,
          };

          // Diff against the last SERVER-CONFIRMED occurrence snapshot, NOT the
          // optimistic cache (activeInstance/currentInstance). The optimistic cache
          // echoes this client's own un-acked writes, so diffing against it makes a
          // forced re-sync produce an EMPTY diff — silently failing to re-push edits
          // that got stuck offline. Diffing against server truth means a re-sync
          // re-includes every un-acked edit until the backend actually confirms it.
          // A brand-new occurrence has no server snapshot yet, so prevMap is {} and
          // the full doc is written (docExists=false path, or full field diff).
          const serverBaseline = serverInstanceRef.current;
          const prevMap = (serverBaseline?.bracketMap && typeof serverBaseline.bracketMap === 'object')
            ? serverBaseline.bracketMap
            : {};

          logSyncDiag('write', { version: localWriteVersionRef.current, instance: selectedInstanceDate });
          await writeInstanceBracketDiff(selectedTournament.id, selectedInstanceDate, {
            prevBracketMap: prevMap,
            nextBracketMap: currentBracketMap,
            matchOrder: currentMatchOrder,
            metadata: instanceMetadata,
            docExists: instanceDocExists,
          });
          lastSyncedBracketMapRef.current = currentBracketMap;

          // Tiny parent-doc summary so the navigator can list occurrences without
          // reading any instance documents. Only write when it actually changed —
          // mid-match score ticks keep the same status/winner/playerCount, so this
          // skips re-delivering the parent snapshot to spectators on every tick.
          const summaryNonBye = bracket.filter(m =>
            m.p1?.name !== 'BYE' && m.p2?.name !== 'BYE' && m.id !== 'gf-1-reset'
          );
          const summary = buildInstanceSummary({
            completed: tournamentComplete,
            inProgress: hasStarted && !tournamentComplete,
            canceled: existingInstance?.canceled || false,
            winner,
            playerCount: players.length,
            matchCount: summaryNonBye.length,
            completedMatches: summaryNonBye.filter(m => m.finished).length,
            tournamentEndTime,
          });
          // Compare on everything except the always-changing lastUpdated stamp.
          const { lastUpdated: _s, ...summaryCmp } = summary;
          const summaryJson = JSON.stringify(summaryCmp);
          if (lastInstanceSummaryRef.current.key !== selectedInstanceDate ||
              lastInstanceSummaryRef.current.json !== summaryJson) {
            await updateInstanceIndex(selectedTournament.id, selectedInstanceDate, summary);
            lastInstanceSummaryRef.current = { key: selectedInstanceDate, json: summaryJson };
          }

          // Keep parent-level settings fresh (customTableNumbers / format), but only
          // when they change — otherwise every tick would rewrite the parent doc.
          const settingsJson = JSON.stringify({ customTableNumbers, tournamentFormat });
          if (lastTournamentSettingsRef.current !== settingsJson) {
            await mergeFirebaseTournament({
              tournamentSettings: { customTableNumbers, tournamentFormat }
            });
            lastTournamentSettingsRef.current = settingsJson;
          }
        } else {
          // Save to main tournament data (for non-recurring tournaments)
          // Calculate if tournament is complete (all non-BYE matches finished)
          const nonByeMatches = bracket.filter(m =>
            m.p1?.name !== 'BYE' && m.p2?.name !== 'BYE' && m.id !== 'gf-1-reset'
          );
          const allMatchesFinished = nonByeMatches.length > 0 && nonByeMatches.every(m => m.finished);
          const hasStarted = bracket.some(m => m.started || m.finished);

          // Determine status
          let status = 'draft';
          if (allMatchesFinished && nonByeMatches.length > 0) {
            status = 'completed';
          } else if (hasStarted) {
            status = 'active';
          }

          // Bump monotonic write version so the listener can reject stale snapshots
          localWriteVersionRef.current += 1;
          expectedAckVersionRef.current = localWriteVersionRef.current;

          const isMigrated = firebaseTournament?.schemaVersion === SCHEMA_VERSION
            || (firebaseTournament?.bracketMap && typeof firebaseTournament.bracketMap === 'object');

          const updates = {
            // Bracket is the source of truth once built (see instance path above).
            players: bracket.length > 0
              ? getBracketPlayers(bracket)
              : playersText.split('\n').filter(Boolean),
            payoutAmounts,
            numPayoutPlaces,
            tournamentSettings: {
              customTableNumbers,
              tournamentFormat
            },
            playerPayments,
            playerPhones,
            playerUserIds,
            textAlertsActivated,
            status,
            clientWriteVersion: localWriteVersionRef.current
          };

          if (isMigrated) {
            const { bracketMap: currentBracketMap, matchOrder: currentMatchOrder } = bracketArrayToMap(bracket);
            const bracketUpdates = diffBracketMaps(
              lastSyncedBracketMapRef.current,
              currentBracketMap,
              'bracketMap'
            );
            Object.assign(updates, bracketUpdates);
            const prevOrder = firebaseTournament?.matchOrder;
            if (!prevOrder || JSON.stringify(prevOrder) !== JSON.stringify(currentMatchOrder)) {
              updates.matchOrder = currentMatchOrder;
            }
            lastSyncedBracketMapRef.current = currentBracketMap;
          } else {
            updates.bracket = bracket;
          }

          await updateFirebaseTournament(updates);
        }

        // Update last synced data and clear local changes flag
        lastSyncedData.current = currentData;
        hasUnsyncedLocalChangesRef.current = false;
        setLastSyncedAt(Date.now());
        setHasSyncError(false);
      } catch (error) {
        // The write failed — keep hasUnsyncedLocalChangesRef = true. Two reasons:
        //   1. The shadow's hadUnsyncedChanges snapshot stays accurate: the
        //      next time it gets re-written we want it to say "yes, unsynced",
        //      so a refresh during a persistent failure can still recover work.
        //   2. The real-time listener at the bracket-update effect gates on
        //      this ref; keeping it true blocks player live-score snapshots
        //      from overwriting the director's still-unsynced edits in local
        //      state. The next successful sync will clear it.
        // justSyncedRef can be cleared safely — it only suppresses one reload.
        justSyncedRef.current = false;
        setHasSyncError(true);
        logSyncDiag('write-error', error?.message || String(error));
        console.error('Error syncing to Firebase:', error);
        alert('Failed to save tournament changes. Please check your internet connection and try again. Your changes may not have been saved.');
      } finally {
        setHasPendingSync(false);
      }
    };

    // If immediate, execute sync right away without debouncing
    if (immediate) {
      performSync();
    } else {
      // Debounce updates to avoid too frequent Firebase writes
      syncTimeoutRef.current = setTimeout(performSync, 1000);
    }
  }, [appMode, selectedTournament?.id, selectedInstanceDate, playersText, bracket, payoutAmounts, numPayoutPlaces, customTableNumbers, tournamentFormat, playerPayments, playerPhones, playerUserIds, textAlertsActivated, updateFirebaseTournament, mergeFirebaseTournament]);
  // NOTE: firebaseTournament?.instances removed from dependencies to prevent sync loops

  // Effect to trigger pending syncs after state updates complete
  useEffect(() => {
    // Skip if manual update (performShuffleBracket) is in progress
    if (manualUpdateInProgressRef.current) return;
    if (pendingSyncRef.current && appMode === 'firebase' && selectedTournament?.id) {
      pendingSyncRef.current = false;
      syncToFirebase();
    }
  }, [appMode, selectedTournament?.id, bracket, playersText, payoutAmounts, numPayoutPlaces, playerUserIds, syncToFirebase]);

  // Keep a ref to the latest syncToFirebase so deferred one-shot writes use current state.
  useEffect(() => {
    syncToFirebaseRef.current = syncToFirebase;
  }, [syncToFirebase]);

  // Actively retry a failed sync until it lands. Previously a write error set the
  // red "Sync failed" LED but nothing re-attempted it — the director had to make
  // another edit to trigger a new sync, so if they'd finished, it silently gave
  // up. Now, while the sync is in an error state, retry on a timer (backing off
  // to a steady cadence) until it succeeds and clears hasSyncError.
  useEffect(() => {
    if (appMode !== 'firebase' || !hasSyncError) return;
    let attempt = 0;
    let timer;
    const schedule = () => {
      // 5s, 10s, 20s, then steady 30s — quick recovery without hammering.
      const delay = Math.min(30000, 5000 * Math.pow(2, attempt));
      attempt++;
      timer = setTimeout(() => {
        if (syncToFirebaseRef.current) syncToFirebaseRef.current(true, true); // force + immediate
        schedule(); // keep retrying until hasSyncError clears and this effect tears down
      }, delay);
    };
    schedule();
    return () => clearTimeout(timer);
  }, [appMode, hasSyncError]);

  // When connectivity returns, immediately flush unsynced work instead of waiting
  // for the next edit or retry tick.
  useEffect(() => {
    if (appMode !== 'firebase' || !isOnline) return;
    if ((hasSyncError || hasUnsyncedLocalChangesRef.current) && syncToFirebaseRef.current) {
      syncToFirebaseRef.current(true, true); // force + immediate
    }
  }, [isOnline, appMode, hasSyncError]);

  // --- Wedged-sync recovery + truthful status --------------------------------
  // Root cause of the venue freezes: with the offline cache, a write resolves
  // against the local cache and never throws, so nothing detected that the
  // ~1-hour auth token had expired (often while the laptop slept) and writes had
  // stopped reaching the server. These effects (1) detect the stall from the
  // server-ack metadata, (2) recover it by refreshing the token + bouncing the
  // Firestore connection, and (3) do so proactively before the token expires.

  // Kick the write channel: refresh the (possibly expired) token, bounce the
  // connection so it reconnects with the fresh token and flushes queued writes,
  // then nudge a sync. Throttled so focus/visibility bursts don't thrash it.
  const lastKickRef = useRef(0);
  const kickConnection = useCallback(async (reason) => {
    const now = Date.now();
    if (now - lastKickRef.current < KICK_THROTTLE_MS) return;
    lastKickRef.current = now;
    logSyncDiag('kick', reason);
    const refreshed = await forceTokenRefresh();
    const reconnected = await forceReconnect();
    if (syncToFirebaseRef.current) syncToFirebaseRef.current(true, true); // force + immediate
    // Authoritatively confirm whether the kick actually drained the write queue
    // (waitForPendingWrites resolves only on real server acks). Diagnostic signal
    // that also tells us when a kick did NOT recover — the case that needs a reload.
    const flushed = await writesAreFlushed(6000);
    logSyncDiag('kick-done', { reason, refreshed, reconnected, flushed });
  }, []);

  // Log every server-ack transition for the active occurrence — the timeline that
  // makes a post-mortem possible (the failure is otherwise silent).
  useEffect(() => {
    if (appMode !== 'firebase' || !selectedInstanceDate) return;
    logSyncDiag('ack', { pending: !!instanceMeta?.hasPendingWrites, fromCache: !!instanceMeta?.fromCache });
  }, [appMode, selectedInstanceDate, instanceMeta?.hasPendingWrites, instanceMeta?.fromCache]);

  // Detect a wedged channel: the server hasn't acked the occurrence's writes for
  // SYNC_STUCK_MS. Each ack (hasPendingWrites -> false) resets the timer, so a
  // healthy connection never trips this; a dead/stale channel latches it.
  useEffect(() => {
    if (!serverAckPending) { setSyncStuck(false); return; }
    const timer = setTimeout(() => {
      setSyncStuck(true);
      logSyncDiag('stuck', { thresholdMs: SYNC_STUCK_MS });
    }, SYNC_STUCK_MS);
    return () => clearTimeout(timer);
  }, [serverAckPending]);

  // Recovery watchdog: while stuck, keep kicking the channel until the server acks
  // (serverAckPending clears -> syncStuck clears -> this tears down). Deliberately
  // NOT gated on hasSyncError — the offline-cache write path never sets it.
  useEffect(() => {
    if (appMode !== 'firebase' || !syncStuck) return;
    kickConnection('watchdog');
    const interval = setInterval(() => kickConnection('watchdog'), WATCHDOG_MS);
    return () => clearInterval(interval);
  }, [appMode, syncStuck, kickConnection]);

  // Proactive token refresh + reconnect while a live occurrence is open, so the
  // ~1-hour token expiry can't silently wedge writes; and on tab-wake (laptop
  // sleep / backgrounding is the classic trigger for a missed refresh).
  useEffect(() => {
    if (appMode !== 'firebase' || !selectedTournament?.id) return;
    const interval = setInterval(() => kickConnection('interval'), TOKEN_REFRESH_MS);
    const onFocus = () => kickConnection('focus');
    const onVisible = () => { if (document.visibilityState === 'visible') kickConnection('visible'); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [appMode, selectedTournament?.id, kickConnection]);

  // Instrument the auth-token lifecycle so a stuck occurrence can be tied to a
  // missed/failed token refresh after the fact.
  useEffect(() => {
    const unsub = onIdTokenChanged(auth, (user) => {
      if (!user) { logSyncDiag('idtoken', 'signed-out'); return; }
      user.getIdTokenResult()
        .then((r) => logSyncDiag('idtoken', { expires: r.expirationTime }))
        .catch(() => {});
    });
    return unsub;
  }, []);

  // NOTE: Removed redundant playerUserIds auto-sync effect - playerUserIds is already in main sync dependencies

  // Cleanup effect: Flush pending sync on component unmount to prevent data loss
  useEffect(() => {
    return () => {
      // If there's a pending debounced sync, execute it immediately before unmount
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
        syncTimeoutRef.current = null;
        // Note: We can't await this, but we trigger it anyway to attempt save
        if (appMode === 'firebase' && selectedTournament?.id) {
          syncToFirebase(true, true); // force=true, immediate=true
        }
      }
    };
  }, [appMode, selectedTournament?.id, syncToFirebase]);

  // Helper function to navigate back to dashboard with immediate sync
  // This ensures any pending changes are saved before navigating away
  const navigateBackToDashboard = useCallback(async () => {
    // Clear any pending debounced sync
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
      syncTimeoutRef.current = null;
    }

    // Force an immediate sync if we're in firebase mode with a tournament
    if (appMode === 'firebase' && selectedTournament?.id) {
      await syncToFirebase(true, true); // force=true, immediate=true
    }

    // Now navigate to dashboard
    setAppMode('dashboard');
    setSelectedTournament(null);
    setSelectedInstanceDate(null);
  }, [appMode, selectedTournament?.id, syncToFirebase]);

  // Calculate tournament rounds
  const rounds = bracket.length > 0
    ? Math.ceil(Math.log2(Math.max(1, playersText.trim().split('\n').filter(Boolean).length)))
    : 1;

  // Function to actually create the tournament
  const createTournamentFromLocal = useCallback(async () => {
    // Check if there's tournament data to save
    const players = playersText.split('\n').filter(Boolean);
    if (players.length < 2) {
      alert('Please add at least 2 players before saving to cloud.');
      return;
    }

    // Create tournament with default values
    const tournamentData = {
      title: `Tournament - ${new Date().toLocaleDateString()}`,
      description: '',
      players: players,
      bracket: bracket,
      payoutAmounts: payoutAmounts,
      numPayoutPlaces: numPayoutPlaces,
      tournamentFormat: tournamentFormat,
      customTableNumbers: customTableNumbers,
      isPublic: false,
      entryFee: 0,
      adminFee: 0
    };

    const result = await createTournament(tournamentData);

    if (result.error) {
      alert('Failed to save tournament to cloud. Please try again.');
      console.error('Error saving tournament:', result.error);
      return;
    }

    // Clear local state and navigate to the created tournament
    setPlayersText('');
    setBracket([]);
    setPayoutAmounts(['']);
    setNumPayoutPlaces(3);

    setSelectedTournament(result.tournament);
    setSelectedInstanceDate(null);
    setAppMode('firebase');

    // Update URL so page refresh keeps us on this tournament
    window.history.pushState({}, '', `/host/${result.tournament.id}`);
  }, [playersText, bracket, payoutAmounts, numPayoutPlaces, tournamentFormat, customTableNumbers, createTournament, setBracket, setNumPayoutPlaces, setPayoutAmounts]);

  // Handle pending save to cloud after authentication
  useEffect(() => {
    if (isAuthenticated && pendingSaveToCloud) {
      // Clear the pending flag first
      setPendingSaveToCloud(false);
      // Directly create the tournament
      createTournamentFromLocal();
    }
  }, [isAuthenticated, pendingSaveToCloud, createTournamentFromLocal]);

  // Save current local tournament to Firebase
  const handleSaveToCloud = async () => {
    if (!isAuthenticated) {
      // Set flag to remember we're trying to save to cloud
      setPendingSaveToCloud(true);
      setShowAuthModal(true);
      return;
    }

    // Call the creation function
    await createTournamentFromLocal();
  };

  // Handle tournament selection from dashboard
  const handleSelectTournament = (tournament) => {
    // Set loading flag - data will be loaded by useEffect when firebaseTournament arrives
    isLoadingFromFirebase.current = true;

    setSelectedTournament(tournament);
    setSelectedInstanceDate(null); // Clear instance selection to show instance navigator for recurring tournaments
    setAppMode('firebase');

    // Update URL so page refresh keeps us on this tournament
    window.history.pushState({}, '', `/host/${tournament.id}`);
  };

  // Handle opening a tournament from admin dashboard for full management
  const handleOpenTournamentFromAdmin = async (tournamentId) => {
    // Enter admin mode and track the tournament being managed
    enterAdminMode(tournamentId);

    // Set loading flag - data will be loaded by useEffect when firebaseTournament arrives
    isLoadingFromFirebase.current = true;

    // Create a minimal tournament object with just the ID
    // The full data will be loaded by the Firebase subscription
    setSelectedTournament({ id: tournamentId });
    setSelectedInstanceDate(null);
    setAppMode('firebase');

    // Update URL so page refresh keeps us on this tournament
    window.history.pushState({}, '', `/host/${tournamentId}`);
  };

  // Safe tournament manager helper functions to prevent mutations
  // One tournament manager per format, reused across the hot bracket-logic paths
  // (finalize/propagate run on every match action) instead of rebuilding it each call.
  const tournamentManager = useMemo(() => createTournamentManager(tournamentFormat), [tournamentFormat]);

  const safeFinalizeByes = (bracket) => {
    const mutableBracket = structuredClone(bracket);
    tournamentManager.finalizeByeMatches(mutableBracket);
    return mutableBracket;
  };

  // Manual BYE finalization for individual matches
  const finalizeSingleBye = (match) => {
    setBracket(prevBracket => {
      const mutableBracket = structuredClone(prevBracket);

      const success = tournamentManager.finalizeSingleByeMatch(mutableBracket, match);
      if (success) {
        // Only auto-assign tables if the checkbox is checked
        if (autoReassignTables) {
          setTimeout(() => performTableAssignment(), 100);
        }
        return mutableBracket;
      }

      // Fallback to original logic if individual finalization fails
      return prevBracket;
    });
  };

  const safePropagateWinners = (bracket) => {
    const mutableBracket = structuredClone(bracket);
    tournamentManager.propagateWinners(mutableBracket);
    return mutableBracket;
  };

  const safeApplyTournamentLogic = (bracket) => {
    const mutableBracket = structuredClone(bracket);

    // Apply multiple passes to ensure all BYE advancements and propagations are fully processed
    // This handles cascading scenarios where propagation creates new BYE advancement opportunities
    let prevBracketState;
    let passes = 0;
    const maxPasses = 10; // Increased limit for complex cascading scenarios

    do {
      prevBracketState = getBracketStateHash(mutableBracket);

      // First finalize any BYE matches that can be auto-advanced
      tournamentManager.finalizeByeMatches(mutableBracket);

      // Then propagate winners/losers from completed matches
      tournamentManager.propagateWinners(mutableBracket);

      // The propagation might have created new BYE vs player scenarios, so we loop again
      passes++;
    } while (getBracketStateHash(mutableBracket) !== prevBracketState && passes < maxPasses);

    return mutableBracket;
  };

  // All the existing tournament management functions remain the same
  const resetTournamentData = async (clearPlayerList = false) => {
    // Block the real-time listener and cancel any pending debounced sync. Without
    // these guards a stale Firestore snapshot (e.g. an in-flight player-score
    // write or a debounced sync about to fire with pre-reset state) can land
    // between local-state-clear and our Firestore write, reloading the old
    // bracket and making the first reset look like it "didn't take" — second
    // reset works only because by then the queue has drained.
    manualUpdateInProgressRef.current = true;
    hasUnsyncedLocalChangesRef.current = true;
    pendingSyncRef.current = false;
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
      syncTimeoutRef.current = null;
    }

    setBracket([]);
    setNodePositions({});
    setPayoutAmounts(['']);
    setNumPayoutPlaces(3);
    setTextAlertsActivated(false); // Reset text alerts flag

    // Delete SMS documents and pending scores for this tournament when resetting
    if (appMode === 'firebase' && firebaseTournament?.id && firebaseTournament?.selfScoringEnabled) {
      // Delete SMS documents
      deleteTournamentSmsDocuments(firebaseTournament.id)
        .catch(err => console.error('Error deleting SMS documents:', err));

      // Delete pending scores (to prevent stale score data showing up after reset)
      deleteAllPendingScores(firebaseTournament.id)
        .catch(err => console.error('Error deleting pending scores:', err));
    }

    let nextPlayersText = playersText;
    if (!clearPlayerList && lastPlayerList) {
      // Deduplicate player names when restoring after reset
      const uniquePlayers = [...new Set(
        lastPlayerList
          .split('\n')
          .map(player => player.trim())
          .filter(player => player)
      )];

      const deduplicatedPlayerList = uniquePlayers
        .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
        .join('\n');

      setPlayersText(deduplicatedPlayerList);
      setLastPlayerList(deduplicatedPlayerList); // Update the stored list too
      nextPlayersText = deduplicatedPlayerList;
    } else if (clearPlayerList) {
      setPlayersText('');
      setLastPlayerList('');
      nextPlayersText = '';
    }

    // Immediate Firebase write (mirrors performShuffleBracket's pattern). Going
    // through the debounced sync would leave a 1s window for stale snapshots
    // to overwrite the reset; writing now closes that window.
    if (appMode === 'firebase' && selectedTournament?.id) {
      localWriteVersionRef.current += 1;
      expectedAckVersionRef.current = localWriteVersionRef.current;
      justSyncedRef.current = true;

      const players = nextPlayersText.split('\n').filter(Boolean);
      // Update the cached bracketMap baseline so the next debounced sync's diff
      // compares against the empty post-reset state, not the pre-reset bracket.
      lastSyncedBracketMapRef.current = {};

      try {
        if (selectedInstanceDate) {
          // Mirror lastInstanceDataHash so the listener's hash comparison sees
          // the snapshot echo as "no change to reload" instead of triggering a
          // reload that would clobber local state.
          lastInstanceDataHash.current = JSON.stringify({
            bracketLength: 0,
            playerCount: players.length,
            textAlertsActivated: false
          });

          // Clear the occurrence's bracket. Diffing prev -> {} emits deleteField()
          // for every match (a plain merge with bracketMap:{} would leave stale
          // match keys behind).
          await writeInstanceBracketDiff(selectedTournament.id, selectedInstanceDate, {
            prevBracketMap: currentInstance?.bracketMap || {},
            nextBracketMap: {},
            matchOrder: [],
            metadata: {
              players,
              playerCount: players.length,
              payoutAmounts: [''],
              numPayoutPlaces: 3,
              completed: false,
              inProgress: false,
              winner: null,
              secondPlace: null,
              thirdPlace: null,
              fourthPlace: null,
              finalStandings: null,
              tournamentEndTime: null,
              totalPrizePool: 0,
              textAlertsActivated: false,
              clientWriteVersion: localWriteVersionRef.current,
            },
            docExists: !!activeInstance,
          });
          await updateInstanceIndex(selectedTournament.id, selectedInstanceDate, buildInstanceSummary({
            completed: false, inProgress: false, canceled: false, winner: null,
            playerCount: players.length, tournamentEndTime: null,
          }));
          await mergeFirebaseTournament({ schemaVersion: SCHEMA_VERSION });
        } else {
          const updates = {
            schemaVersion: SCHEMA_VERSION,
            players,
            bracketMap: {},
            matchOrder: [],
            payoutAmounts: [''],
            numPayoutPlaces: 3,
            textAlertsActivated: false,
            status: 'draft',
            clientWriteVersion: localWriteVersionRef.current
          };
          await updateFirebaseTournament(updates);
        }
      } catch (err) {
        console.error('Error syncing tournament reset to Firebase:', err);
      } finally {
        manualUpdateInProgressRef.current = false;
      }
    } else {
      manualUpdateInProgressRef.current = false;
    }
  };

  // [All other existing functions remain exactly the same]
  // ... (keeping all the existing tournament logic)

  // Navigation handler
  const handleNavigate = async (view) => {
    // Exit admin mode when navigating away from managed tournament
    if (isAdminMode && view !== 'firebase') {
      exitAdminMode();
    }

    // If navigating away from a Firebase tournament, force immediate sync first
    if (appMode === 'firebase' && selectedTournament?.id && (view === 'landing' || view === 'finder' || view === 'dashboard' || view === 'admin')) {
      // Clear any pending debounced sync
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
        syncTimeoutRef.current = null;
      }
      // Force an immediate sync
      await syncToFirebase(true, true); // force=true, immediate=true
    }

    if (view === 'landing') {
      setAppMode('landing');
      setSelectedTournament(null);
      setSelectedInstanceDate(null);
      window.history.pushState({}, '', '/');
    } else if (view === 'finder') {
      setAppMode('finder');
      setSelectedTournament(null);
      setSelectedInstanceDate(null);
      window.history.pushState({}, '', '/find');
    } else if (view === 'leaderboard') {
      setAppMode('leaderboard');
      setSelectedTournament(null);
      setSelectedInstanceDate(null);
      window.history.pushState({}, '', '/leaderboard');
    } else if (view === 'dashboard') {
      setAppMode('dashboard');
      setSelectedTournament(null);
      setSelectedInstanceDate(null);
      window.history.pushState({}, '', '/dashboard');
    } else if (view === 'admin') {
      setAppMode('admin');
      setSelectedTournament(null);
      setSelectedInstanceDate(null);
      window.history.pushState({}, '', '/admin');
    } else if (view === 'local') {
      setAppMode('local');
      setSelectedTournament(null);
      setSelectedInstanceDate(null);
      // Local tournaments don't have SMS/self-scoring, so reset auto-assign
      setAutoReassignTables(false);
      window.history.pushState({}, '', '/local');
    } else if (view === 'contact') {
      setAppMode('contact');
      window.history.pushState({}, '', '/contact');
    } else if (view === 'privacy') {
      setAppMode('privacy');
      window.history.pushState({}, '', '/privacy');
    } else if (view === 'terms') {
      setAppMode('terms');
      window.history.pushState({}, '', '/terms');
    } else if (view === 'help') {
      setAppMode('help');
      window.history.pushState({}, '', '/help');
    } else if (view === 'profile') {
      setAppMode('profile');
    }
  };

  // Share modal handler
  const handleShowShare = () => {
    setShowShareModal(true);
  };

  // Restore last player list
  useEffect(() => {
    if (isInitialLoad.current && !playersText && lastPlayerList) {
      setPlayersText(lastPlayerList);
    }
    isInitialLoad.current = false;
  }, [lastPlayerList, playersText]);

  // Restore players from bracket (only on initial load)
  const hasRestoredPlayers = useRef(false);
  useEffect(() => {
    if (!playersText && bracket.length > 0 && !hasRestoredPlayers.current) {
      const playerNames = [];
      bracket.forEach(match => {
        if (match.round === 1) {
          if (match.p1?.name && !match.p1.name.includes('Winner of') && !match.p1.name.includes('Loser of') && match.p1.name !== 'BYE') {
            playerNames.push(match.p1.name);
          }
          if (match.p2?.name && !match.p2.name.includes('Winner of') && !match.p2.name.includes('Loser of') && match.p2.name !== 'BYE') {
            playerNames.push(match.p2.name);
          }
        }
      });

      if (playerNames.length > 0) {
        setPlayersText(playerNames.join('\n'));
        setLastPlayerList(playerNames.join('\n'));
        hasRestoredPlayers.current = true;
      }
    }
  }, [bracket, playersText, setLastPlayerList]);

  // Reset player restoration flag when switching tournaments
  // This ensures the restoration logic works correctly for each tournament
  useEffect(() => {
    hasRestoredPlayers.current = false;
  }, [selectedTournament?.id, selectedInstanceDate]);

  // Recalculate positions (only when bracket length changes, not on every bracket update)
  const lastBracketLength = useRef(0);
  useEffect(() => {
    if (bracket.length > 0 && (Object.keys(nodePositions).length === 0 || lastBracketLength.current !== bracket.length)) {
      const positions = getBracketNodePositions(bracket, rounds);
      setNodePositions(positions);
      lastBracketLength.current = bracket.length;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bracket, rounds, setNodePositions, nodePositions]);

  // Force table view for Round Robin format
  useEffect(() => {
    const tournamentManager = createTournamentManager(tournamentFormat);
    if (!tournamentManager.hasBracketView() && viewMode === 'bracket') {
      setViewMode('table');
    }
  }, [tournamentFormat, viewMode, setViewMode]);

  // Note: Table assignment is now handled directly in specific functions rather than through triggers

  // Note: Table reassignment on settings changes is handled manually through the "Re-assign All Tables" button
  // to give users control over when reassignment happens

  // Helper function to assign tables to matches using utility (for new tournaments and filling gaps)
  const performTableAssignment = useCallback(() => {
    // SET FLAG BEFORE LOCAL CHANGE to prevent real-time listener from overwriting
    hasUnsyncedLocalChangesRef.current = true;

    setBracket(prevBracket => {
      const tableSettings = {
        customTableNumbers
      };

      return assignTablesToMatches(prevBracket, tableSettings);
    });
  }, [customTableNumbers, setBracket]);

  // Helper function to shuffle table assignments randomly (for "Re-assign All Tables" button)
  const performTableShuffle = useCallback(() => {
    // SET FLAG BEFORE LOCAL CHANGE to prevent real-time listener from overwriting
    hasUnsyncedLocalChangesRef.current = true;

    setBracket(prevBracket => {
      const tableSettings = {
        customTableNumbers
      };

      return shuffleTableAssignments(prevBracket, tableSettings);
    });
  }, [customTableNumbers, setBracket]);

  // Helper function to clear table assignments from waiting matches
  const clearTables = useCallback(() => {
    // SET FLAG BEFORE LOCAL CHANGE to prevent real-time listener from overwriting
    hasUnsyncedLocalChangesRef.current = true;

    setBracket(prevBracket => {
      return prevBracket.map(match => {
        // Only clear tables from matches that are waiting (not started and not finished)
        if (!match.started && !match.finished) {
          return { ...match, table: '' };
        }
        return match;
      });
    });
  }, [setBracket]);

  // [Continue with all existing functions - handlePlayersChange, updateMatch, markFinished, etc.]
  // For brevity, I'll include the essential ones:

  const handlePlayersChange = (e) => {
    setPlayersText(e.target.value);
    if (e.target.value.trim()) {
      setLastPlayerList(e.target.value);
    }
  };

  const updateMatch = (matchId, updates) => {
    // Block reassigning a table that an in-progress match is actively using.
    // Checked here (against the always-current bracketRef) rather than in
    // MatchBox, whose memoized `bracket` prop can be stale and falsely flag a
    // table that was already freed by a finished match.
    if (updates.table !== undefined && updates.table.trim() !== '') {
      const inProgressConflict = bracketRef.current.some(m =>
        m.id !== matchId &&
        m.table === updates.table &&
        m.started &&
        !m.finished
      );
      if (inProgressConflict) {
        alert(`Table ${updates.table} is currently in use by a match that has started. Please use a different table.`);
        return false;
      }
    }

    // SET FLAG BEFORE LOCAL CHANGE to prevent real-time listener from overwriting
    hasUnsyncedLocalChangesRef.current = true;

    setBracket(prevBracket => {
      // Check if this is a table assignment update
      if (updates.table !== undefined) {
        const tableSettings = {
          customTableNumbers
        };

        // Use smart reassignment to handle duplicates
        let newBracket = reassignTablesAfterManualChange(prevBracket, tableSettings, matchId, updates.table);

        // SMS is now sent only when director clicks "Start Match"
        // Tokens are generated when tournament starts (in handleMatchUpdatesForSmsAlerts)

        return newBracket;
      } else {
        // Regular update for non-table changes
        return prevBracket.map(match =>
          match.id === matchId ? { ...match, ...updates } : match
        );
      }
    });
    return true;
  };

  const startMatch = (matchId) => {
    // Find the match to get player info for SMS
    const matchToStart = bracket.find(m => m.id === matchId);

    // SET FLAG BEFORE LOCAL CHANGE to prevent real-time listener from overwriting
    hasUnsyncedLocalChangesRef.current = true;

    setBracket(prevBracket =>
      prevBracket.map(match =>
        match.id === matchId ? { ...match, started: true, actualStartTime: Date.now() } : match
      )
    );
    // Trigger Firebase sync for real-time updates
    if (appMode === 'firebase' && selectedTournament?.id) {
      pendingSyncRef.current = true;
    }

    // Send SMS notifications when director starts a match
    // SMS is sent if self-scoring is enabled and match has tokens/phone numbers
    const selfScoringEnabled = firebaseTournament?.selfScoringEnabled || false;
    const instanceData = currentInstance;

    if (selfScoringEnabled && matchToStart && firebaseTournament?.id) {
      const player1Name = matchToStart.player1 || matchToStart.p1?.name;
      const player2Name = matchToStart.player2 || matchToStart.p2?.name;
      const currentPlayerPhones = instanceData?.playerPhones || playerPhones || {};
      const tournamentName = firebaseTournament.title || 'Tournament';

      // Send SMS to player 1
      if (player1Name && currentPlayerPhones[player1Name]?.phone && matchToStart.p1Token) {
        sendMatchReadyNotification({
          tournamentId: firebaseTournament.id,
          tournamentName,
          matchId: matchToStart.id,
          playerPhone: currentPlayerPhones[player1Name].phone,
          playerName: player1Name,
          opponentName: player2Name,
          token: matchToStart.p1Token,
          tableNumber: matchToStart.table,
          instanceDate: selectedInstanceDate
        }).catch(err => console.error(`SMS failed for ${player1Name}:`, err));
      }

      // Send SMS to player 2
      if (player2Name && currentPlayerPhones[player2Name]?.phone && matchToStart.p2Token) {
        sendMatchReadyNotification({
          tournamentId: firebaseTournament.id,
          tournamentName,
          matchId: matchToStart.id,
          playerPhone: currentPlayerPhones[player2Name].phone,
          playerName: player2Name,
          opponentName: player1Name,
          token: matchToStart.p2Token,
          tableNumber: matchToStart.table,
          instanceDate: selectedInstanceDate
        }).catch(err => console.error(`SMS failed for ${player2Name}:`, err));
      }
    }
  };

  const unstartMatch = (matchId) => {
    // SET FLAG BEFORE LOCAL CHANGE to prevent real-time listener from overwriting
    hasUnsyncedLocalChangesRef.current = true;

    setBracket(prevBracket =>
      prevBracket.map(match =>
        match.id === matchId ? { ...match, started: false, actualStartTime: null } : match
      )
    );
    // Trigger Firebase sync for real-time updates
    if (appMode === 'firebase' && selectedTournament?.id) {
      pendingSyncRef.current = true;
    }
  };

  const markFinished = (match) => {
    const score1 = Number(match.score1) || 0;
    const score2 = Number(match.score2) || 0;

    if (score1 === score2) {
      alert('Match cannot end in a tie. Please enter different scores.');
      return;
    }

    const winner = score1 > score2 ? match.p1.name : match.p2.name;
    const freedTable = match.table; // Capture the table that will be freed

    // SET FLAG BEFORE LOCAL CHANGE to prevent real-time listener from overwriting
    hasUnsyncedLocalChangesRef.current = true;

    setBracket(prevBracket => {
      // Only copy the match that's being updated - KEEP the table for display
      // Clear scoreConflict when match is manually resolved by director
      const newBracket = prevBracket.map(m =>
        m.id === match.id ? { ...m, finished: true, winner, status: "", actualEndTime: Date.now(), scoreConflict: false } : m
      );

      // Apply tournament logic safely
      let result = safeApplyTournamentLogic(newBracket);

      // If auto-reassignment is enabled and there was a table assigned to this match,
      // assign it to the next available match
      // The table is now "freed" because the match is finished, even though it still shows the table number
      if (autoReassignTables && freedTable && freedTable !== "") {
        result = assignFreedTable(result, freedTable);
      }

      // If self-scoring is enabled, enrich any newly ready matches with tokens
      // SMS is now sent only when director clicks "Start Match"
      const selfScoringEnabled = firebaseTournament?.selfScoringEnabled || false;
      if (selfScoringEnabled && firebaseTournament?.id) {
        result = enrichMatchesForSelfScoring(
          result,
          playerPhones,
          firebaseTournament.id
        );
      }

      return result;
    });

    // Set flag to trigger sync after state update completes
    if (appMode === 'firebase' && selectedTournament?.id) {
      pendingSyncRef.current = true;
    }
  };

  // DEBUG: Auto-complete tournament for testing (call window.autoCompleteTournament() in console)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  window.autoCompleteTournament = () => {
    // SET FLAG BEFORE LOCAL CHANGE to prevent real-time listener from overwriting
    hasUnsyncedLocalChangesRef.current = true;

    setBracket(prevBracket => {
      const newBracket = structuredClone(prevBracket);
      const tournamentManager = createTournamentManager(tournamentFormat);

      let updated = true;
      let passes = 0;
      const maxPasses = 200;

      while (updated && passes < maxPasses) {
        updated = false;
        passes++;

        // First finalize BYEs
        tournamentManager.finalizeByeMatches(newBracket);
        tournamentManager.propagateWinners(newBracket);

        // Find next playable match and complete it
        for (const match of newBracket) {
          if (match.finished) continue;

          const p1 = match.p1?.name;
          const p2 = match.p2?.name;

          if (!p1 || !p2) continue;
          if (p1.includes('Winner of') || p1.includes('Loser of')) continue;
          if (p2.includes('Winner of') || p2.includes('Loser of')) continue;
          if (p1 === 'BYE' || p2 === 'BYE') continue;

          // Random winner
          const winner = Math.random() > 0.5 ? p1 : p2;
          match.finished = true;
          match.winner = winner;
          match.score1 = winner === p1 ? '7' : String(Math.floor(Math.random() * 6));
          match.score2 = winner === p2 ? '7' : String(Math.floor(Math.random() * 6));
          match.actualEndTime = Date.now();
          updated = true;
          break; // Only complete one match per pass to ensure proper propagation
        }

        tournamentManager.propagateWinners(newBracket);
      }

      return newBracket;
    });
  };

  const undoFinished = (match) => {
    // SET FLAG BEFORE LOCAL CHANGE to prevent real-time listener from overwriting
    hasUnsyncedLocalChangesRef.current = true;

    setBracket(prevBracket => {
      // Check if this is a BYE match - use individual undo for BYE matches
      const isByeMatch = match.winner === "BYE" || match.p1.name === "BYE" || match.p2.name === "BYE";

      if (isByeMatch) {
        // Use individual BYE undo for precise control
        const mutableBracket = structuredClone(prevBracket);
        const tournamentManager = createTournamentManager(tournamentFormat);

        if (tournamentManager.undoSingleByeMatch) {
          const success = tournamentManager.undoSingleByeMatch(mutableBracket, match);
          if (success) {
            return mutableBracket;
          }
        }

        // Fallback to original logic if individual undo not available
      }

      const undoneMatchNum = match.matchNum;
      const loser = match.p1.name === match.winner ? match.p2.name : match.p1.name;

      // Process all matches immutably
      const newBracket = prevBracket.map(m => {
        // Step 1: Reset the undone match
        if (m.id === match.id) {
          return {
            ...m,
            finished: false,
            winner: '',
            status: 'Pending',
            score1: '0',
            score2: '0',
            manuallyFinalized: false
          };
        }

        // Step 2: Handle other matches that may need updates
        let updatedMatch = { ...m };
        let needsUpdate = false;

        // Check if player 1 needs to be reset
        // Only reset if the player actually came from the undone match (has ref)
        if (m.p1?.name === match.winner && m.p1?.ref && m.p1.ref.includes(`M${undoneMatchNum}`)) {
          updatedMatch.p1 = { name: m.p1.ref, ref: m.p1.ref };
          needsUpdate = true;
        }
        if (m.p1?.name === loser && m.p1?.ref && m.p1.ref.includes(`M${undoneMatchNum}`)) {
          updatedMatch.p1 = { name: m.p1.ref, ref: m.p1.ref };
          needsUpdate = true;
        }

        // Check if player 2 needs to be reset
        // Only reset if the player actually came from the undone match (has ref)
        if (m.p2?.name === match.winner && m.p2?.ref && m.p2.ref.includes(`M${undoneMatchNum}`)) {
          updatedMatch.p2 = { name: m.p2.ref, ref: m.p2.ref };
          needsUpdate = true;
        }
        if (m.p2?.name === loser && m.p2?.ref && m.p2.ref.includes(`M${undoneMatchNum}`)) {
          updatedMatch.p2 = { name: m.p2.ref, ref: m.p2.ref };
          needsUpdate = true;
        }

        // Reset only matches that were updated above (those that reference the undone match)
        // This prevents resetting earlier matches that happen to have the same player names
        if (needsUpdate && m.finished) {
          updatedMatch.finished = false;
          updatedMatch.winner = '';
          updatedMatch.status = 'Pending';
          updatedMatch.score1 = '0';
          updatedMatch.score2 = '0';
          updatedMatch.manuallyFinalized = false;
        }

        return needsUpdate ? updatedMatch : m;
      });

      // Step 3: Re-propagate all remaining finished matches safely
      // For non-BYE matches, use full propagation but avoid automatic BYE finalization
      let result;
      if (isByeMatch) {
        // For BYE matches, just return the bracket without any automatic processing
        result = newBracket;
      } else {
        result = safePropagateWinners(newBracket);
      }

      return result;
    });
  };

  const handleShuffleBracket = () => {
    // Button is disabled in Sidebar when real matches have started,
    // so no warning needed here - just shuffle directly
    const shouldShuffle = bracket.length > 0;
    performShuffleBracket(shouldShuffle);
  };

  // Helper function to add tokens and phone data to matches for self-scoring
  // Only generates tokens - does NOT send SMS (that happens when text alerts are activated)
  const enrichMatchesForSelfScoring = (bracketArray, playerPhonesMap, tournamentId) => {
    const selfScoringEnabled = firebaseTournament?.selfScoringEnabled || false;
    if (!selfScoringEnabled) return bracketArray;

    return bracketArray.map((match, index) => {
      // Check if match is ready for scoring (both players assigned, not BYE)
      const player1Name = match.player1 || match.p1?.name;
      const player2Name = match.player2 || match.p2?.name;
      const hasPlayer1 = player1Name && player1Name !== 'BYE' && player1Name !== 'TBD' && !player1Name.includes('Winner of') && !player1Name.includes('Loser of');
      const hasPlayer2 = player2Name && player2Name !== 'BYE' && player2Name !== 'TBD' && !player2Name.includes('Winner of') && !player2Name.includes('Loser of');

      if (hasPlayer1 && hasPlayer2 && !match.finished && !match.p1Token) {
        // Generate tokens for this match
        const tokens = generateMatchTokens(tournamentId, index);

        // Get phone data for both players
        const p1PhoneData = playerPhonesMap[player1Name];
        const p2PhoneData = playerPhonesMap[player2Name];

        return {
          ...match,
          p1Token: tokens.p1Token,
          p2Token: tokens.p2Token,
          tokenCreatedAt: tokens.tokenCreatedAt,
          p1Phone: p1PhoneData?.phone || null,
          p2Phone: p2PhoneData?.phone || null,
          p1PhoneMasked: p1PhoneData?.phoneMasked || null,
          p2PhoneMasked: p2PhoneData?.phoneMasked || null,
          smsSent: {
            p1: false,
            p2: false,
            p1Name: null,
            p2Name: null
          }
        };
      }

      return match;
    });
  };


  const performShuffleBracket = async (shouldShuffle = false) => {
    const playerNames = playersText.trim().split('\n').filter(Boolean).map(name => capitalizeName(name.trim()));

    if (playerNames.length >= 3) {
      // Block all Firebase sync effects during manual tournament start
      // This prevents race conditions where old state could overwrite new bracket
      manualUpdateInProgressRef.current = true;

      if (bracket.length === 0) {
        setLastPlayerList(playersText);
      }

      const finalPlayers = shouldShuffle ? shuffleArray(playerNames) : playerNames;
      const tournamentManager = createTournamentManager(tournamentFormat);
      const newBracket = tournamentManager.buildBracket(finalPlayers);

      // Finalize BYE matches safely
      let finalizedBracket = safeFinalizeByes(newBracket);

      // Note: Tables are NOT auto-assigned here. Director assigns tables manually or via
      // the "Auto-assign tables" checkbox. SMS alerts include the table number when the
      // director starts each match.

      // If self-scoring is enabled, add tokens and phone data to ready matches
      const selfScoringEnabled = firebaseTournament?.selfScoringEnabled || false;
      if (selfScoringEnabled && firebaseTournament?.id) {
        finalizedBracket = enrichMatchesForSelfScoring(
          finalizedBracket,
          playerPhones,
          firebaseTournament.id
        );
      }

      // Reset text alerts activated flag when bracket is shuffled
      setTextAlertsActivated(false);

      // Reset auto-assign tables to unchecked when starting a new tournament
      setAutoReassignTables(false);

      // Clear any pending sync from a previous reset to prevent race conditions
      pendingSyncRef.current = false;

      // SET FLAG BEFORE LOCAL CHANGE to prevent real-time listener from overwriting
      hasUnsyncedLocalChangesRef.current = true;

      setBracket(finalizedBracket);

      const newRounds = Math.ceil(Math.log2(finalPlayers.length));
      const positions = getBracketNodePositions(finalizedBracket, newRounds);
      setNodePositions(positions);

      // Trigger immediate Firebase sync for tournament start with the new bracket data (including tables)
      if (appMode === 'firebase' && selectedInstanceDate) {
        // Cancel any pending debounced sync — its stale closure (with empty bracket)
        // would otherwise fire after this immediate write and overwrite the new bracket.
        if (syncTimeoutRef.current) {
          clearTimeout(syncTimeoutRef.current);
          syncTimeoutRef.current = null;
        }

        const players = playerNames;
        const existingInstance = currentInstance;

        // Bump monotonic write version so the listener can reject stale snapshots
        localWriteVersionRef.current += 1;
        expectedAckVersionRef.current = localWriteVersionRef.current;

        // Generating a new bracket is the one case where we always rewrite the
        // entire bracket — its structure just changed. Write the v2 schema
        // (bracketMap + matchOrder) and drop any legacy bracket array. This
        // also opportunistically migrates pre-migration tournaments.
        const { bracketMap: newBracketMap, matchOrder: newMatchOrder } = bracketArrayToMap(finalizedBracket);

        const instanceData = {
          players,
          playerCount: players.length,
          bracketMap: newBracketMap,
          matchOrder: newMatchOrder,
          payoutAmounts: payoutAmounts,
          numPayoutPlaces: numPayoutPlaces,
          format: tournamentFormat,
          completed: false,
          inProgress: true, // Tournament just started
          winner: null,
          canceled: existingInstance?.canceled || false,
          cancelReason: existingInstance?.cancelReason || null,
          playerPayments: playerPayments || {},
          playerPhones: selfScoringEnabled ? playerPhones : {},
          playerUserIds: playerUserIds || {},
          textAlertsActivated: false,
          clientWriteVersion: localWriteVersionRef.current,
          lastUpdated: new Date().toISOString()
        };

        // Prevent Firebase listener from reloading old data during write
        justSyncedRef.current = true;
        lastInstanceDataHash.current = JSON.stringify({
          bracketLength: finalizedBracket.length,
          playerCount: players.length,
          textAlertsActivated: false
        });
        // Mirror lastSyncedBracketMapRef so the next debounced sync diffs
        // against what we just wrote, not the pre-shuffle bracket.
        lastSyncedBracketMapRef.current = newBracketMap;

        try {
          // Start always writes the full occurrence doc (structure just changed).
          await setInstance(selectedTournament.id, selectedInstanceDate, instanceData);
          await updateInstanceIndex(selectedTournament.id, selectedInstanceDate, buildInstanceSummary({
            completed: false, inProgress: true, canceled: existingInstance?.canceled || false,
            winner: null, playerCount: players.length, tournamentEndTime: null,
            matchCount: finalizedBracket.filter(m =>
              m.p1?.name !== 'BYE' && m.p2?.name !== 'BYE' && m.id !== 'gf-1-reset'
            ).length,
            completedMatches: 0,
          }));
          await mergeFirebaseTournament({ schemaVersion: SCHEMA_VERSION });
        } finally {
          manualUpdateInProgressRef.current = false;
        }
      } else if (appMode === 'firebase' && selectedTournament?.id) {
        // For non-recurring tournaments, also do immediate sync
        // This prevents race conditions with pending debounced syncs from reset
        // Clear any pending sync timeout to prevent old empty bracket from syncing
        if (syncTimeoutRef.current) {
          clearTimeout(syncTimeoutRef.current);
          syncTimeoutRef.current = null;
        }

        const selfScoringEnabled = firebaseTournament?.selfScoringEnabled || false;
        // Bump monotonic write version so the listener can reject stale snapshots
        localWriteVersionRef.current += 1;
        expectedAckVersionRef.current = localWriteVersionRef.current;

        // New bracket structure — write v2 schema (bracketMap + matchOrder).
        const { bracketMap: newBracketMap, matchOrder: newMatchOrder } = bracketArrayToMap(finalizedBracket);

        const updates = {
          schemaVersion: SCHEMA_VERSION,
          players: playerNames,
          bracketMap: newBracketMap,
          matchOrder: newMatchOrder,
          payoutAmounts,
          numPayoutPlaces,
          tournamentSettings: {
            customTableNumbers,
            tournamentFormat
          },
          playerPayments: playerPayments || {},
          playerPhones: selfScoringEnabled ? playerPhones : {},
          playerUserIds: playerUserIds || {},
          textAlertsActivated: false,
          status: 'active',
          clientWriteVersion: localWriteVersionRef.current
        };

        // Prevent Firebase listener from reloading old data during write
        justSyncedRef.current = true;
        lastSyncedBracketMapRef.current = newBracketMap;

        try {
          await updateFirebaseTournament(updates);
        } finally {
          manualUpdateInProgressRef.current = false;
        }
      } else {
        // Non-Firebase mode - clear flag immediately
        manualUpdateInProgressRef.current = false;
      }
    } else {
      // Show error for insufficient players
      const minPlayers = 3; // Minimum 3 players required for a meaningful tournament bracket
      alert(`Please enter at least ${minPlayers} players to start a tournament. You currently have ${playerNames.length} player${playerNames.length === 1 ? '' : 's'}.`);
    }
  };

  // DEBUG: Dump player scoring URLs to the console whenever the bracket changes.
  // Used for testing the version guard / SMS self-scoring flow without sending texts.
  // Run `copy(window.__scoringUrls)` in the console to put the list on the clipboard.
  // TODO: Remove before deploying to production.
  useEffect(() => {
    if (bracket?.length && selectedTournament?.id) {
      const origin = window.location.origin;
      const urls = bracket
        .filter(m => m.p1Token || m.p2Token)
        .flatMap(m => [
          m.p1Token && { match: m.id, player: m.p1?.name, url: `${origin}/score/${selectedTournament.id}/${m.p1Token}` },
          m.p2Token && { match: m.id, player: m.p2?.name, url: `${origin}/score/${selectedTournament.id}/${m.p2Token}` }
        ])
        .filter(Boolean);
      if (urls.length) {
        console.table(urls);
        window.__scoringUrls = urls;
      }
    }
  }, [bracket, selectedTournament?.id]);

  const handleSwapPlayers = (player1, player2) => {
    if (player1 === player2) return;

    // SET FLAG BEFORE LOCAL CHANGE to prevent real-time listener from overwriting
    hasUnsyncedLocalChangesRef.current = true;

    setBracket(prevBracket => {
      const newBracket = swapPlayersInMatches(prevBracket, player1, player2);

      // Apply tournament logic safely after swapping
      return safeApplyTournamentLogic(newBracket);
    });

    if (player1 !== "BYE" && player2 !== "BYE") {
      setPlayersText(prevText => {
        const lines = prevText.split('\n');
        const updatedLines = lines.map(line => {
          const trimmedLine = line.trim();
          if (trimmedLine === player1) return player2;
          if (trimmedLine === player2) return player1;
          return line;
        });
        return updatedLines.join('\n');
      });
    }
  };

  // Stable-identity wrappers for the handlers passed into the memoized bracket/table
  // components, so a parent re-render doesn't invalidate their memo. Each always runs
  // the latest closure above (no stale state).
  const stableUpdateMatch = useStableCallback(updateMatch);
  const stableStartMatch = useStableCallback(startMatch);
  const stableUnstartMatch = useStableCallback(unstartMatch);
  const stableMarkFinished = useStableCallback(markFinished);
  const stableUndoFinished = useStableCallback(undoFinished);
  const stableFinalizeSingleBye = useStableCallback(finalizeSingleBye);
  const stableSwapPlayers = useStableCallback(handleSwapPlayers);

  const handleCancelInstance = async (instanceDate, reason, shouldCancel = true) => {
    if (!firebaseTournament || !selectedTournament?.id) {
      return;
    }

    // The navigator is the source of truth for listing, so cancel state lives in
    // the parent `instanceIndex`. A future occurrence may have no instance doc yet.
    const idx = firebaseTournament.instanceIndex?.[instanceDate] || {};
    const restoredStatus = idx.completed ? 'completed' : (idx.inProgress ? 'inProgress' : 'scheduled');
    await updateInstanceIndex(selectedTournament.id, instanceDate, {
      canceled: shouldCancel,
      cancelReason: shouldCancel ? reason : null,
      status: shouldCancel ? 'canceled' : restoredStatus,
      lastUpdated: new Date().toISOString(),
    });

    // Best-effort: reflect on the occurrence document if it already exists.
    try {
      await writeInstanceBracketDiff(selectedTournament.id, instanceDate, {
        metadata: { canceled: shouldCancel, cancelReason: shouldCancel ? reason : null },
        docExists: true,
      });
    } catch (err) {
      // No occurrence doc yet (future occurrence) — the index entry is authoritative.
    }
  };

  const handleSaveBranding = async (updatedBranding) => {
    if (!firebaseTournament || !selectedTournament?.id) {
      throw new Error('No tournament loaded');
    }

    try {
      await updateFirebaseTournament({
        branding: updatedBranding
      });
    } catch (error) {
      console.error('Error updating branding:', error);
      throw error;
    }
  };


  // Main render logic
  if (authLoading) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'var(--bg-primary)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>🔄</div>
          <h3 style={{ margin: 0, color: 'var(--text-primary)' }}>Loading...</h3>
        </div>
      </div>
    );
  }

  // Admin Dashboard
  if (appMode === 'admin') {
    return (
      <AdminDashboard
        onClose={() => handleNavigate('dashboard')}
        onOpenTournament={handleOpenTournamentFromAdmin}
      />
    );
  }

  // Landing Page (including Dashboard view)
  if (appMode === 'landing' || appMode === 'dashboard') {
    const showDashboard = appMode === 'dashboard';

    return (
      <>
        <LandingNavigation
          currentView={showDashboard ? "dashboard" : "landing"}
          onNavigate={handleNavigate}
          onShowAuth={(mode) => {
            setAuthModalMode(mode || 'login');
            setShowAuthModal(true);
          }}
          onSaveToCloud={handleSaveToCloud}
          onShowProfile={() => setShowProfilePage(true)}
          onCreateTournament={() => {
            setOpenCreateModalOnDashboard(true);
            setAppMode('dashboard');
          }}
        />
        <LandingPage
          onNavigate={handleNavigate}
          onShowAuth={(mode) => {
            setAuthModalMode(mode || 'login');
            setShowAuthModal(true);
          }}
          showDashboard={showDashboard}
          onSelectTournament={handleSelectTournament}
          openTournamentFinder={openFinderOnDashboard}
          openCreateModal={openCreateModalOnDashboard}
          onNavigateToAdmin={() => handleNavigate('admin')}
        />
        {showAuthModal && (
          <AuthModal
            isOpen={showAuthModal}
            initialMode={authModalMode}
            onClose={() => setShowAuthModal(false)}
            onAuthSuccess={() => {
              setAppMode('dashboard');
              setShowAuthModal(false);
            }}
          />
        )}
        {showProfilePage && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'var(--bg-modal-overlay)',
            zIndex: 1000,
            overflow: 'auto'
          }}>
            <div style={{
              backgroundColor: 'var(--bg-secondary)',
              borderRadius: window.innerWidth <= 768 ? '0' : '12px',
              maxWidth: '800px',
              width: window.innerWidth <= 768 ? '100%' : 'auto',
              margin: window.innerWidth <= 768 ? '0' : '5vh auto',
              position: 'relative',
              minHeight: window.innerWidth <= 768 ? '100vh' : 'auto',
              maxHeight: window.innerWidth <= 768 ? '100vh' : '90vh',
              overflow: 'auto'
            }}>
              <button
                onClick={handleCloseProfile}
                style={{
                  position: 'sticky',
                  top: '16px',
                  left: 'calc(100% - 80px)',
                  marginTop: '16px',
                  marginBottom: '-48px',
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  zIndex: 1001,
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(0,0,0,0.1)',
                  float: 'right'
                }}
                title="Close Profile"
              >
                ×
              </button>
              <ProfilePage onDirtyChange={handleProfileDirty} highlightSaveTrigger={highlightSaveTrigger} />
            </div>
          </div>
        )}
        {showShareModal && selectedTournament && (
          <ShareTournamentModal
            isOpen={showShareModal}
            tournament={selectedTournament}
            onClose={() => setShowShareModal(false)}
            instanceDate={selectedInstanceDate}
          />
        )}
      </>
    );
  }

  // Tournament Finder (Public Access)
  if (appMode === 'player') {
    return (
      <>
        <LandingNavigation
          currentView="leaderboard"
          onNavigate={handleNavigate}
          onShowAuth={(mode) => {
            setAuthModalMode(mode || 'login');
            setShowAuthModal(true);
          }}
          onShowProfile={() => setShowProfilePage(true)}
          onCreateTournament={() => {
            setOpenCreateModalOnDashboard(true);
            setAppMode('dashboard');
          }}
        />
        <div style={{ marginTop: '64px', minHeight: 'calc(100vh - 64px)', overflowY: 'auto' }}>
          <PlayerProfile userId={viewPlayerId} onBack={() => handleNavigate('leaderboard')} />
        </div>
        {showAuthModal && (
          <AuthModal
            isOpen={showAuthModal}
            initialMode={authModalMode}
            onClose={() => setShowAuthModal(false)}
            onAuthSuccess={() => {
              setAppMode('dashboard');
              setShowAuthModal(false);
            }}
          />
        )}
      </>
    );
  }

  if (appMode === 'leaderboard') {
    return (
      <>
        <LandingNavigation
          currentView="leaderboard"
          onNavigate={handleNavigate}
          onShowAuth={(mode) => {
            setAuthModalMode(mode || 'login');
            setShowAuthModal(true);
          }}
          onShowProfile={() => setShowProfilePage(true)}
          onCreateTournament={() => {
            setOpenCreateModalOnDashboard(true);
            setAppMode('dashboard');
          }}
        />
        <div style={{ marginTop: '64px', minHeight: 'calc(100vh - 64px)', overflowY: 'auto' }}>
          <Leaderboard />
        </div>
        {showAuthModal && (
          <AuthModal
            isOpen={showAuthModal}
            initialMode={authModalMode}
            onClose={() => setShowAuthModal(false)}
            onAuthSuccess={() => {
              setAppMode('dashboard');
              setShowAuthModal(false);
            }}
          />
        )}
      </>
    );
  }

  if (appMode === 'finder') {
    return (
      <>
        <LandingNavigation
          currentView="finder"
          onNavigate={handleNavigate}
          onShowAuth={(mode) => {
            setAuthModalMode(mode || 'login');
            setShowAuthModal(true);
          }}
          onShowProfile={() => setShowProfilePage(true)}
          onCreateTournament={() => {
            setOpenCreateModalOnDashboard(true);
            setAppMode('dashboard');
          }}
        />
        <div style={{ marginTop: '64px', height: 'calc(100vh - 64px)' }}>
          <TournamentFinder />
        </div>
        {showAuthModal && (
          <AuthModal
            isOpen={showAuthModal}
            initialMode={authModalMode}
            onClose={() => setShowAuthModal(false)}
            onAuthSuccess={() => {
              setAppMode('dashboard');
              setShowAuthModal(false);
            }}
          />
        )}
        {showProfilePage && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'var(--bg-modal-overlay)',
            zIndex: 1000,
            overflow: 'auto'
          }}>
            <div style={{
              backgroundColor: 'var(--bg-secondary)',
              borderRadius: window.innerWidth <= 768 ? '0' : '12px',
              maxWidth: '800px',
              width: window.innerWidth <= 768 ? '100%' : 'auto',
              margin: window.innerWidth <= 768 ? '0' : '5vh auto',
              position: 'relative',
              minHeight: window.innerWidth <= 768 ? '100vh' : 'auto',
              maxHeight: window.innerWidth <= 768 ? '100vh' : '90vh',
              overflow: 'auto'
            }}>
              <button
                onClick={handleCloseProfile}
                style={{
                  position: 'sticky',
                  top: '16px',
                  left: 'calc(100% - 80px)',
                  marginTop: '16px',
                  marginBottom: '-48px',
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  zIndex: 1001,
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(0,0,0,0.1)',
                  float: 'right'
                }}
                title="Close Profile"
              >
                ×
              </button>
              <ProfilePage onDirtyChange={handleProfileDirty} highlightSaveTrigger={highlightSaveTrigger} />
            </div>
          </div>
        )}
      </>
    );
  }

  // Contact Page
  if (appMode === 'contact') {
    return (
      <>
        <LandingNavigation
          currentView="contact"
          onNavigate={handleNavigate}
          onShowAuth={(mode) => {
            setAuthModalMode(mode || 'login');
            setShowAuthModal(true);
          }}
          onShowProfile={() => setShowProfilePage(true)}
          onCreateTournament={() => {
            setOpenCreateModalOnDashboard(true);
            setAppMode('dashboard');
          }}
        />
        <ContactPage onBack={() => handleNavigate('landing')} />
        {showAuthModal && (
          <AuthModal
            isOpen={showAuthModal}
            initialMode={authModalMode}
            onClose={() => setShowAuthModal(false)}
            onAuthSuccess={() => {
              setAppMode('dashboard');
              setShowAuthModal(false);
            }}
          />
        )}
        {showProfilePage && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'var(--bg-modal-overlay)',
            zIndex: 1000,
            overflow: 'auto'
          }}>
            <div style={{
              backgroundColor: 'var(--bg-secondary)',
              borderRadius: window.innerWidth <= 768 ? '0' : '12px',
              maxWidth: '800px',
              width: window.innerWidth <= 768 ? '100%' : 'auto',
              margin: window.innerWidth <= 768 ? '0' : '5vh auto',
              position: 'relative',
              minHeight: window.innerWidth <= 768 ? '100vh' : 'auto',
              maxHeight: window.innerWidth <= 768 ? '100vh' : '90vh',
              overflow: 'auto'
            }}>
              <button
                onClick={handleCloseProfile}
                style={{
                  position: 'sticky',
                  top: '16px',
                  left: 'calc(100% - 80px)',
                  marginTop: '16px',
                  marginBottom: '-48px',
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  zIndex: 1001,
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(0,0,0,0.1)',
                  float: 'right'
                }}
                title="Close Profile"
              >
                ×
              </button>
              <ProfilePage onDirtyChange={handleProfileDirty} highlightSaveTrigger={highlightSaveTrigger} />
            </div>
          </div>
        )}
      </>
    );
  }

  // Privacy Page
  if (appMode === 'privacy') {
    return (
      <>
        <LandingNavigation
          currentView="privacy"
          onNavigate={handleNavigate}
          onShowAuth={(mode) => {
            setAuthModalMode(mode || 'login');
            setShowAuthModal(true);
          }}
          onShowProfile={() => setShowProfilePage(true)}
          onCreateTournament={() => {
            setOpenCreateModalOnDashboard(true);
            setAppMode('dashboard');
          }}
        />
        <PrivacyPage onBack={() => handleNavigate('landing')} onNavigate={handleNavigate} />
        {showAuthModal && (
          <AuthModal
            isOpen={showAuthModal}
            initialMode={authModalMode}
            onClose={() => setShowAuthModal(false)}
            onAuthSuccess={() => {
              setAppMode('dashboard');
              setShowAuthModal(false);
            }}
          />
        )}
        {showProfilePage && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'var(--bg-modal-overlay)',
            zIndex: 1000,
            overflow: 'auto'
          }}>
            <div style={{
              backgroundColor: 'var(--bg-secondary)',
              borderRadius: window.innerWidth <= 768 ? '0' : '12px',
              maxWidth: '800px',
              width: window.innerWidth <= 768 ? '100%' : 'auto',
              margin: window.innerWidth <= 768 ? '0' : '5vh auto',
              position: 'relative',
              minHeight: window.innerWidth <= 768 ? '100vh' : 'auto',
              maxHeight: window.innerWidth <= 768 ? '100vh' : '90vh',
              overflow: 'auto'
            }}>
              <button
                onClick={handleCloseProfile}
                style={{
                  position: 'sticky',
                  top: '16px',
                  left: 'calc(100% - 80px)',
                  marginTop: '16px',
                  marginBottom: '-48px',
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  zIndex: 1001,
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(0,0,0,0.1)',
                  float: 'right'
                }}
                title="Close Profile"
              >
                ×
              </button>
              <ProfilePage onDirtyChange={handleProfileDirty} highlightSaveTrigger={highlightSaveTrigger} />
            </div>
          </div>
        )}
      </>
    );
  }

  // Terms Page
  if (appMode === 'terms') {
    return (
      <>
        <LandingNavigation
          currentView="terms"
          onNavigate={handleNavigate}
          onShowAuth={(mode) => {
            setAuthModalMode(mode || 'login');
            setShowAuthModal(true);
          }}
          onShowProfile={() => setShowProfilePage(true)}
          onCreateTournament={() => {
            setOpenCreateModalOnDashboard(true);
            setAppMode('dashboard');
          }}
        />
        <TermsPage onBack={() => handleNavigate('landing')} onNavigate={handleNavigate} />
        {showAuthModal && (
          <AuthModal
            isOpen={showAuthModal}
            initialMode={authModalMode}
            onClose={() => setShowAuthModal(false)}
            onAuthSuccess={() => {
              setAppMode('dashboard');
              setShowAuthModal(false);
            }}
          />
        )}
        {showProfilePage && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'var(--bg-modal-overlay)',
            zIndex: 1000,
            overflow: 'auto'
          }}>
            <div style={{
              backgroundColor: 'var(--bg-secondary)',
              borderRadius: window.innerWidth <= 768 ? '0' : '12px',
              maxWidth: '800px',
              width: window.innerWidth <= 768 ? '100%' : 'auto',
              margin: window.innerWidth <= 768 ? '0' : '5vh auto',
              position: 'relative',
              minHeight: window.innerWidth <= 768 ? '100vh' : 'auto',
              maxHeight: window.innerWidth <= 768 ? '100vh' : '90vh',
              overflow: 'auto'
            }}>
              <button
                onClick={handleCloseProfile}
                style={{
                  position: 'sticky',
                  top: '16px',
                  left: 'calc(100% - 80px)',
                  marginTop: '16px',
                  marginBottom: '-48px',
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  zIndex: 1001,
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(0,0,0,0.1)',
                  float: 'right'
                }}
                title="Close Profile"
              >
                ×
              </button>
              <ProfilePage onDirtyChange={handleProfileDirty} highlightSaveTrigger={highlightSaveTrigger} />
            </div>
          </div>
        )}
      </>
    );
  }

  // Help Page
  if (appMode === 'help') {
    return (
      <>
        <LandingNavigation
          currentView="help"
          onNavigate={handleNavigate}
          onShowAuth={(mode) => {
            setAuthModalMode(mode || 'login');
            setShowAuthModal(true);
          }}
          onShowProfile={() => setShowProfilePage(true)}
          onCreateTournament={() => {
            setOpenCreateModalOnDashboard(true);
            setAppMode('dashboard');
          }}
        />
        <HelpPage onBack={() => handleNavigate('landing')} />
        {showAuthModal && (
          <AuthModal
            isOpen={showAuthModal}
            initialMode={authModalMode}
            onClose={() => setShowAuthModal(false)}
            onAuthSuccess={() => {
              setAppMode('dashboard');
              setShowAuthModal(false);
            }}
          />
        )}
        {showProfilePage && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'var(--bg-modal-overlay)',
            zIndex: 1000,
            overflow: 'auto'
          }}>
            <div style={{
              backgroundColor: 'var(--bg-secondary)',
              borderRadius: window.innerWidth <= 768 ? '0' : '12px',
              maxWidth: '800px',
              width: window.innerWidth <= 768 ? '100%' : 'auto',
              margin: window.innerWidth <= 768 ? '0' : '5vh auto',
              position: 'relative',
              minHeight: window.innerWidth <= 768 ? '100vh' : 'auto',
              maxHeight: window.innerWidth <= 768 ? '100vh' : '90vh',
              overflow: 'auto'
            }}>
              <button
                onClick={handleCloseProfile}
                style={{
                  position: 'sticky',
                  top: '16px',
                  left: 'calc(100% - 80px)',
                  marginTop: '16px',
                  marginBottom: '-48px',
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  zIndex: 1001,
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(0,0,0,0.1)',
                  float: 'right'
                }}
                title="Close Profile"
              >
                ×
              </button>
              <ProfilePage onDirtyChange={handleProfileDirty} highlightSaveTrigger={highlightSaveTrigger} />
            </div>
          </div>
        )}
      </>
    );
  }

  // Player Stats Profile view
  if (appMode === 'profile') {
    return (
      <PlayerProfile
        onBack={() => setAppMode('dashboard')}
        onNavigate={(mode) => setAppMode(mode)}
      />
    );
  }

  // Public tournament view
  if (appMode === 'public' && publicShareUrl) {
    return (
      <PublicTournamentView
        shareUrl={publicShareUrl}
        onClose={() => {
          // The header "← Back" always lands on the tournament finder, regardless
          // of how the viewer arrived (shared link opened in a fresh tab, from the
          // finder, etc.) so public visitors get a consistent way to browse other
          // tournaments rather than being bounced out of the site.
          sessionStorage.removeItem('cameFromTournamentFinder');
          setPublicShareUrl(null);
          setSelectedInstanceDate(null);
          setAppMode('finder');
          window.history.pushState({}, '', '/find');
        }}
      />
    );
  }

  // Player self-scoring view
  if (appMode === 'scoring' && scoringToken) {
    return <PlayerScoreEntry token={scoringToken} tournamentId={scoringTournamentId} />;
  }

  // Streaming overlay view (for OBS/streaming software)
  if (appMode === 'overlay' && overlayTournamentId) {
    return <StreamingOverlay tournamentId={overlayTournamentId} tableNumber={overlayTableNumber} />;
  }

  // Check if tournament has scheduling (scheduledDateTime field)
  // Check selectedTournament first to avoid flash, then use firebaseTournament data when available
  const tournamentData = firebaseTournament || selectedTournament;
  const hasScheduling = tournamentData?.scheduledDateTime || tournamentData?.recurring?.enabled;

  if (appMode === 'firebase' && selectedTournament && hasScheduling && !selectedInstanceDate) {
    // Show ScheduledTournamentView when no instance is selected (show instance navigator)

    return (
      <>
        <ScheduledTournamentView
          tournament={tournamentData}
          onBack={navigateBackToDashboard}
          onEditBranding={() => {
            setShowBrandingModal(true);
          }}
          onSelectInstance={(instanceDate) => {
            // Navigate to bracket view for this specific instance
            // The loading effect will handle initializing future instances
            setSelectedInstanceDate(instanceDate);
            // Update URL to include instance date for page refresh persistence
            window.history.pushState({}, '', `/host/${selectedTournament.id}/${instanceDate}`);
          }}
          onCancelInstance={handleCancelInstance}
          isOwner={true}
          instanceIndex={tournamentData.instanceIndex || {}}
          instances={tournamentData.instances || {}}
          currentInstanceDate={selectedInstanceDate}
        />

        {/* Branding Editor Modal */}
        {showBrandingModal && (firebaseTournament || selectedTournament) && (
          <BrandingModal
            tournament={firebaseTournament || selectedTournament}
            onClose={() => setShowBrandingModal(false)}
            onSave={handleSaveBranding}
          />
        )}
      </>
    );
  }

  // Main tournament view (local or Firebase with selected instance)
  return (
    <div style={{
      fontFamily: 'Arial, sans-serif',
      height: '100vh',
      overflow: 'hidden'
    }}>
      {/* Top Navigation - Use LandingNavigation for all modes */}
      <LandingNavigation
        currentView={appMode === 'firebase' ? 'tournament' : appMode}
        onNavigate={handleNavigate}
        onShowAuth={(mode) => {
          setAuthModalMode(mode || 'login');
          setShowAuthModal(true);
        }}
        onSaveToCloud={handleSaveToCloud}
        onShowShare={appMode === 'firebase' && firebaseTournament?.isPublic ? handleShowShare : null}
        onShowProfile={() => setShowProfilePage(true)}
        syncStatus={appMode === 'firebase' ? syncStatus : undefined}
        lastSyncedAt={appMode === 'firebase' ? lastSyncedAt : undefined}
      />

      {showSyncAlert && (
        <SyncAlertBanner offline={!isOnline} onReload={handleSyncReload} />
      )}

      {/* Main Layout Container */}
      <div style={{
        display: 'flex',
        height: 'calc(100vh - 64px)',
        marginTop: '64px'
      }}>
      {/* Loading Screen for Firebase Tournament */}
      {appMode === 'firebase' && tournamentLoading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            fontSize: '48px',
            marginBottom: '20px',
            animation: 'spin 1s linear infinite'
          }}>
            🎱
          </div>
          <div style={{
            fontSize: '18px',
            color: '#667eea',
            fontWeight: 'bold'
          }}>
            Loading tournament...
          </div>
          <style>{`
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      )}


      {/* Sidebar */}
      <Sidebar
        tournamentTitle={appMode === 'firebase' && firebaseTournament ? firebaseTournament.title : null}
        playersText={playersText}
        handlePlayersChange={handlePlayersChange}
        handlePlayersBlur={() => {
          if (bracket.length === 0) {
            const cleaned = playersText.trim().split('\n').filter(Boolean).map(name => capitalizeName(name.trim())).join('\n');
            setPlayersText(cleaned);
          }
        }}
        handleShuffleBracket={handleShuffleBracket}
        shuffleButtonText={bracket.length === 0 ? "Start Tournament" : "Shuffle Bracket"}
        exportTournament={() => {
          const tournamentData = {
            playersText,
            bracket,
            payoutAmounts,
            numPayoutPlaces,
            tournamentFormat,
            exportDate: new Date().toISOString()
          };
          const dataStr = JSON.stringify(tournamentData, null, 2);
          const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
          const exportFileDefaultName = `tournament_${new Date().toISOString().split('T')[0]}.json`;
          const linkElement = document.createElement('a');
          linkElement.setAttribute('href', dataUri);
          linkElement.setAttribute('download', exportFileDefaultName);
          linkElement.click();
        }}
        importTournament={(e) => {
          const file = e.target.files[0];
          if (!file) return;
          const reader = new FileReader();
          reader.onload = (event) => {
            try {
              const tournamentData = JSON.parse(event.target.result);
              setPlayersText(tournamentData.playersText || '');
              setBracket(tournamentData.bracket || []);
              setPayoutAmounts(tournamentData.payoutAmounts || ['']);
              setNumPayoutPlaces(tournamentData.numPayoutPlaces || 3);
              setTournamentFormat(tournamentData.tournamentFormat || TOURNAMENT_FORMATS.DOUBLE_ELIMINATION);
              if (tournamentData.bracket && tournamentData.bracket.length > 0) {
                const positions = getBracketNodePositions(tournamentData.bracket, rounds);
                setNodePositions(positions);
              }
            } catch (error) {
              alert('Error loading tournament file: ' + error.message);
            }
          };
          reader.readAsText(file);
          e.target.value = '';
        }}
        fileInputRef={fileInputRef}
        bracket={bracket}
        payoutAmounts={payoutAmounts}
        setPayoutAmounts={setPayoutAmounts}
        numPayoutPlaces={numPayoutPlaces}
        setNumPayoutPlaces={setNumPayoutPlaces}
        simulateTournament={() => {
          setBracket(prevBracket => {
            let currentBracket = [...prevBracket];
            let hasMoreMatches = true;
            let iterationCount = 0;
            const maxIterations = 100; // Increased safety limit

            // Simulate tournament match by match, ensuring proper winner propagation
            while (hasMoreMatches && iterationCount < maxIterations) {
              hasMoreMatches = false;
              iterationCount++;

              // Find matches that can be simulated (have actual player names, not references)
              const matchesToSimulate = currentBracket.filter(match =>
                !match.finished &&
                match.p1?.name &&
                match.p2?.name &&
                // Ensure we have actual player names or BYE, not references
                (!match.p1.name.includes('Winner of') && !match.p1.name.includes('Loser of')) &&
                (!match.p2.name.includes('Winner of') && !match.p2.name.includes('Loser of')) &&
                match.p1.name && match.p2.name
              );

              if (matchesToSimulate.length > 0) {
                hasMoreMatches = true;

                // Simulate only ONE match at a time to ensure proper propagation
                const matchToSimulate = matchesToSimulate[0];

                // Simulate this single match
                currentBracket = currentBracket.map(match => {
                  if (match.id === matchToSimulate.id) {
                    let score1 = Math.floor(Math.random() * 6);
                    let score2 = Math.floor(Math.random() * 6);
                    while (score1 === score2) {
                      score2 = Math.floor(Math.random() * 6);
                    }
                    return {
                      ...match,
                      score1: score1.toString(),
                      score2: score2.toString(),
                      finished: true,
                      winner: score1 > score2 ? match.p1.name : match.p2.name,
                      status: ""
                    };
                  }
                  return match;
                });

                // Apply tournament logic after each individual match to ensure proper propagation
                currentBracket = safeApplyTournamentLogic(currentBracket);

                // Force multiple propagation passes to ensure all references are resolved
                let propagationPasses = 0;
                const maxPropagationPasses = 5;
                while (propagationPasses < maxPropagationPasses) {
                  const beforePropagation = JSON.stringify(currentBracket);
                  currentBracket = safeApplyTournamentLogic(currentBracket);
                  const afterPropagation = JSON.stringify(currentBracket);

                  // If no changes occurred, propagation is complete
                  if (beforePropagation === afterPropagation) {
                    break;
                  }
                  propagationPasses++;
                }
              }
            }

            return currentBracket;
          });
        }}
        handleSidebarPlayerNameChange={(oldName, newName, playerIndex = null) => {
          if (!newName.trim() || oldName === newName) return;
          const cleanNewName = newName.trim();

          // SET FLAG BEFORE LOCAL CHANGES to prevent real-time listener from overwriting
          // the rename before the debounced sync flushes it.
          hasUnsyncedLocalChangesRef.current = true;

          // Update playersText - replace old name with new name
          setPlayersText(prev => {
            const lines = prev.split('\n');
            const updatedLines = lines.map(line =>
              line === oldName ? cleanNewName : line
            );
            return updatedLines.join('\n');
          });

          // Update bracket - replace old name with new name in all matches
          setBracket(currentBracket => {
            return currentBracket.map(match => {
              const updatedMatch = { ...match };

              // Update player1/p1
              if (match.player1 === oldName) updatedMatch.player1 = cleanNewName;
              if (match.p1?.name === oldName) updatedMatch.p1 = { ...match.p1, name: cleanNewName };

              // Update player2/p2
              if (match.player2 === oldName) updatedMatch.player2 = cleanNewName;
              if (match.p2?.name === oldName) updatedMatch.p2 = { ...match.p2, name: cleanNewName };

              // Update winner
              if (match.winner === oldName) updatedMatch.winner = cleanNewName;

              return updatedMatch;
            });
          });

          // Update playerPayments if old name had a payment record
          setPlayerPayments(prev => {
            if (oldName in prev) {
              const updated = { ...prev };
              updated[cleanNewName] = updated[oldName];
              delete updated[oldName];
              return updated;
            }
            return prev;
          });

          // Update playerPhones if old name had a phone record
          setPlayerPhones(prev => {
            if (oldName in prev) {
              const updated = { ...prev };
              updated[cleanNewName] = updated[oldName];
              delete updated[oldName];
              return updated;
            }
            return prev;
          });

          // Update playerUserIds if old name had a user ID record
          setPlayerUserIds(prev => {
            if (oldName in prev) {
              const updated = { ...prev };
              updated[cleanNewName] = updated[oldName];
              delete updated[oldName];
              return updated;
            }
            return prev;
          });
        }}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        assignTables={performTableShuffle} // Shuffle for manual button
        assignTablesNoShuffle={performTableAssignment} // For auto-assign checkbox
        clearTables={clearTables} // Clear tables from waiting matches
        customTableNumbers={customTableNumbers}
        setCustomTableNumbers={setCustomTableNumbers}
        autoReassignTables={autoReassignTables}
        setAutoReassignTables={setAutoReassignTables}
        resetTournamentData={resetTournamentData}
        tournamentFormat={tournamentFormat}
        onTournamentFormatChange={setTournamentFormat}
        lastPlayerList={lastPlayerList}
        tournament={appMode === 'firebase' && firebaseTournament ? firebaseTournament : { format: tournamentFormat, raceLength, expectedTimePerMatch }}
        raceLength={raceLength}
        setRaceLength={setRaceLength}
        expectedTimePerMatch={expectedTimePerMatch}
        setExpectedTimePerMatch={setExpectedTimePerMatch}
        selectedInstanceDate={selectedInstanceDate}
        playerPayments={playerPayments}
        setPlayerPayments={setPlayerPayments}
        playerPhones={playerPhones}
        onPlayerPhoneChange={(playerName, phoneData) => {
          if (phoneData === null) {
            // Remove entry
            setPlayerPhones(prev => {
              const updated = { ...prev };
              delete updated[playerName];
              return updated;
            });
          } else {
            // Check if this is a NEW phone number (not already in the system)
            const existingPhone = playerPhones[playerName]?.phone;
            const newPhone = phoneData.phone;
            const isNewOptIn = newPhone && newPhone !== existingPhone;

            // Add/update entry
            setPlayerPhones(prev => ({
              ...prev,
              [playerName]: phoneData
            }));

            // Send opt-in confirmation for NEW phone numbers only
            if (isNewOptIn && firebaseTournament?.selfScoringEnabled) {
              sendOptInConfirmation({
                playerPhone: newPhone,
                playerName: playerName,
                tournamentId: firebaseTournament?.id
              }).catch(err => {
                console.error(`Error sending opt-in confirmation to ${playerName}:`, err);
              });
            }
          }
        }}
        playerUserIds={playerUserIds}
        onPlayerUserIdChange={(playerName, userId) => {
          if (userId === null) {
            // Remove entry
            setPlayerUserIds(prev => {
              const updated = { ...prev };
              delete updated[playerName];
              return updated;
            });
          } else {
            // Add/update entry
            setPlayerUserIds(prev => {
              const updated = { ...prev, [playerName]: userId };
              return updated;
            });
          }
        }}
        selfScoringEnabled={firebaseTournament?.selfScoringEnabled || false}
      />

      {/* Main Content */}
      <div
        id="main-content-container"
        style={{
          flex: 1,
          overflow: viewMode === 'bracket' ? 'hidden' : 'auto',
          padding: '0px',
          marginLeft: !isMobile && sidebarOpen ? '270px' : '0px',
          transition: 'margin-left 0.3s ease',
          position: 'relative'
        }}>



        {/* Navigation and View Toggle Buttons */}
        {bracket.length > 0 && (
          <div style={{
            position: 'fixed',
            top: '84px', // 64px for navigation bar + 20px spacing
            right: '20px',
            zIndex: 1500,
            display: 'flex',
            gap: '8px',
            flexWrap: 'wrap',
            justifyContent: 'flex-end'
          }}>
            {(() => {
              const tournamentManager = createTournamentManager(tournamentFormat);

              const buttonStyle = {
                padding: '8px 12px',
                fontSize: '13px',
                fontWeight: 'bold',
                borderRadius: '6px',
                borderWidth: '1px',
                borderStyle: 'solid',
                boxShadow: '1px 1px 4px #ddd',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                minWidth: '70px',
                textAlign: 'center'
              };

              return (
                <>
                  {/* Legend Toggle Button - appears left of Winners button (hidden for Round Robin) */}
                  {tournamentManager.hasBracketView() && (
                    <button
                      onClick={() => setShowLegend(!showLegend)}
                      style={{
                        ...buttonStyle,
                        backgroundColor: showLegend ? '#6c757d' : '#007bff',
                        color: '#fff',
                        borderColor: showLegend ? '#5a6268' : '#0056b3',
                        minWidth: '40px',
                        padding: '8px 10px'
                      }}
                      title={showLegend ? 'Hide Legend' : 'Show Legend'}
                    >
                      {showLegend ? '🔼' : '🔽'}
                    </button>
                  )}

                  {tournamentManager.hasTableView() && tournamentManager.hasBracketView() && (
                    <button
                      onClick={() => setViewMode(viewMode === 'bracket' ? 'table' : 'bracket')}
                      style={{
                        ...buttonStyle,
                        padding: '10px 16px',
                        fontSize: '14px',
                        borderRadius: '8px',
                        backgroundColor: viewMode === 'bracket' ? '#17a2b8' : '#28a745',
                        color: '#fff',
                        borderWidth: '2px',
                        borderColor: '#000000',
                        boxShadow: '2px 2px 7px #ddd',
                        minWidth: 'auto'
                      }}
                      title={`Switch to ${viewMode === 'bracket' ? 'table' : 'bracket'} view`}
                    >
                      {viewMode === 'bracket' ? '📊 Table View' : '🌳 Bracket View'}
                    </button>
                  )}
                </>
              );
            })()}
          </div>
        )}

        {/* Match State Legend - positioned below navigation buttons */}
        {bracket.length > 0 && showLegend && (
          <div style={{
            position: 'fixed',
            top: isMobile ? '135px' : '140px', // Below the navigation buttons
            left: isMobile ? '10px' : 'auto',
            right: isMobile ? '10px' : '20px',
            zIndex: 1400,
            backgroundColor: 'var(--bg-card)',
            border: '1px solid var(--border-color)',
            borderRadius: '8px',
            padding: '8px 12px',
            boxShadow: 'var(--shadow-md)',
            display: 'flex',
            gap: '12px',
            alignItems: 'center',
            fontSize: '12px',
            fontWeight: 'bold',
            flexWrap: 'wrap',
            justifyContent: isMobile ? 'center' : 'flex-start',
            color: 'var(--text-primary)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <div style={{ width: 16, height: 3, backgroundColor: 'var(--success)', borderRadius: 1 }}></div>
              <span>Finished</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <div style={{ width: 16, height: 3, backgroundColor: '#007bff', borderRadius: 1 }}></div>
              <span>Playing</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <div style={{ width: 16, height: 3, backgroundColor: '#ff9800', borderRadius: 1 }}></div>
              <span>Waiting</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <div style={{ width: 16, height: 3, backgroundColor: '#6c757d', borderRadius: 1 }}></div>
              <span>Pending Players</span>
            </div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              marginLeft: '8px',
              paddingLeft: '8px',
              borderLeft: '1px solid var(--border-color)'
            }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer',
                userSelect: 'none'
              }}>
                <input
                  type="checkbox"
                  checked={hideBYEMatches}
                  onChange={(e) => setHideBYEMatches(e.target.checked)}
                  style={{ cursor: 'pointer' }}
                />
                <span>Hide BYE matches</span>
              </label>
            </div>
          </div>
        )}

        {/* Drag and Drop Notice */}
        {bracket.length > 0 && canRearrangePlayers(bracket) && showDragDropTip && (
          <div style={{
            position: 'fixed',
            top: '180px', // Position below the legend
            right: '20px',
            zIndex: 1400,
            padding: '10px 15px',
            paddingRight: '35px', // Make room for close button
            backgroundColor: 'var(--success-bg)',
            border: '1px solid var(--success)',
            borderRadius: '6px',
            color: 'var(--success-text)',
            fontSize: '14px',
            fontWeight: 'bold',
            textAlign: 'center',
            boxShadow: 'var(--shadow-sm)',
            maxWidth: '300px',
            lineHeight: '1.3'
          }}>
            <button
              onClick={() => setShowDragDropTip(false)}
              style={{
                position: 'absolute',
                top: '4px',
                right: '4px',
                background: 'none',
                border: 'none',
                fontSize: '18px',
                cursor: 'pointer',
                color: 'var(--success-text)',
                width: '24px',
                height: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                padding: 0,
                lineHeight: 1,
                fontWeight: 'bold',
                opacity: 0.7,
                transition: 'opacity 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.opacity = '1'}
              onMouseLeave={(e) => e.target.style.opacity = '0.7'}
              title="Close tip"
              aria-label="Close tip"
            >
              ×
            </button>
            💡 {isMobile ? 'Tap and hold' : 'Drag and drop'} player names to rearrange matchups before any games are played!
          </div>
        )}

        {/* Tournament Content */}
        {bracket.length > 0 ? (
          (() => {
            const tournamentManager = createTournamentManager(tournamentFormat);
            const shouldShowBracket = viewMode === 'bracket' && tournamentManager.hasBracketView();

            return shouldShowBracket ? (
              <div
                style={{
                  width: '100%',
                  height: 'calc(100vh - 64px)', // 64px nav only
                  overflow: 'scroll',
                  position: 'relative',
                  backgroundColor: 'var(--bg-bracket)'
                }}
                className="bracket-scroll-container"
              >
                <Bracket
                  bracket={bracket}
                  nodePositions={nodePositions}
                  updateMatch={stableUpdateMatch}
                  startMatch={stableStartMatch}
                  unstartMatch={stableUnstartMatch}
                  markFinished={stableMarkFinished}
                  undoFinished={stableUndoFinished}
                  finalizeSingleBye={stableFinalizeSingleBye}
                  onSwapPlayers={stableSwapPlayers}
                  hideBYEMatches={hideBYEMatches}
                  isDoubles={isDoublesFormat}
                  selfScoringEnabled={firebaseTournament?.selfScoringEnabled || false}
                  raceSettings={raceSettings}
                />
              </div>
            ) : (
              <TableView
                bracket={bracket}
                updateMatch={stableUpdateMatch}
                startMatch={stableStartMatch}
                unstartMatch={stableUnstartMatch}
                markFinished={stableMarkFinished}
                undoFinished={stableUndoFinished}
                finalizeSingleBye={stableFinalizeSingleBye}
                tournamentFormat={tournamentFormat}
                onSwapPlayers={stableSwapPlayers}
                hideBYEMatches={hideBYEMatches}
                raceSettings={raceSettings}
                isDoubles={isDoublesFormat}
              />
            );
          })()
        ) : null}
      </div> {/* Main Content */}

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onAuthSuccess={() => {
          // Only switch to dashboard if not trying to save to cloud
          if (!pendingSaveToCloud) {
            setAppMode('dashboard');
          }
        }}
      />

      {/* Share Tournament Modal */}
      <ShareTournamentModal
        isOpen={showShareModal}
        tournament={appMode === 'firebase' && firebaseTournament ? firebaseTournament : null}
        onClose={() => setShowShareModal(false)}
        instanceDate={selectedInstanceDate}
      />

      {/* Branding Editor Modal */}
      {showBrandingModal && (firebaseTournament || selectedTournament) && (
        <BrandingModal
          tournament={firebaseTournament || selectedTournament}
          onClose={() => setShowBrandingModal(false)}
          onSave={handleSaveBranding}
        />
      )}

      {/* Profile Page Modal */}
      {showProfilePage && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'var(--bg-modal-overlay)',
          zIndex: 2000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            backgroundColor: 'var(--bg-secondary)',
            borderRadius: '8px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflow: 'auto',
            position: 'relative'
          }}>
            {/* Close Button */}
            <button
              onClick={() => setShowProfilePage(false)}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                zIndex: 2001,
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                backgroundColor: 'rgba(0,0,0,0.1)'
              }}
              title="Close Profile"
            >
              ×
            </button>

            <ProfilePage />
          </div>
        </div>
      )}

      </div> {/* Close main layout container */}

    </div>
  );
}

// Lightweight fallback shown while a code-split chunk loads.
function RouteFallback() {
  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-secondary)',
      fontFamily: 'var(--font-display)', fontSize: 14
    }}>
      <div style={{
        width: 28, height: 28, border: '3px solid var(--border-color)',
        borderTopColor: 'var(--accent-primary)', borderRadius: '50%',
        animation: 'spin 0.8s linear infinite'
      }} />
    </div>
  );
}

// Main App component with AuthProvider
export default function App() {
  return (
    <AuthProvider>
      <Suspense fallback={<RouteFallback />}>
        <TournamentApp />
      </Suspense>
    </AuthProvider>
  );
}